// FP 보정 간트 차트 — 자원 간트(`report/ResourceGantt.js`)와 **같은 프레임·같은 렌더러** 위에
// **편집 레이어**만 얹은 화면이다(reports/gantt-implementation.md §6).
//
// 자원 간트와 갈리는 지점만 먼저 적는다(나머지는 그쪽 주석이 그대로 유효하다):
//   1. **데이터가 일부 다르다.** 활동이 `ActivityDto`가 아니라 `AdjustmentsDto`라 결품·Tool 사용
//      정보가 없다 → 결품 마름모·Tool 마크는 이 화면에 없다. 반면 **비가동은 2026-09-08에 자원
//      간트와 같은 형태로 통일됐다** — 서버가 기준정보(`tb_fp_resource_calendar`, 자원별 1회씩
//      조회하던 N+1)가 아니라 계획 결과(`tb_fp_resrc_downtime`, 벌크 1회)에서 읽어
//      `downtimes`/`divideYDowntimes` 배열을 주므로, 두 화면이 같은 휴무를 같은 렌더러로 그린다.
//   2. **툴팁이 서버를 치지 않는다.** old `OnGanttTip`이 `runBox.Start`/`activityData`만 읽었고,
//      편집으로 값이 바뀌는 화면이라 서버 응답을 쓰면 **이동 전 값이 뜬다**(§6-6). 진실 소스는
//      `rawRows` 하나이고 툴팁·상세탭·좌측 강조가 전부 여기서 파생된다.
//   3. **의존선이 없다.** old 보정 화면은 `activity-relations`를 부르지 않는다.
//   4. **편집이 있다.** 드래그 이동 / 버튼 이동 / 되돌리기. 저장·서버 재배치·전체 밀기는 old·new
//      백엔드 양쪽에서 제거된 기능이라 "미지원" 메시지로 남는다(§6-1).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";

import { withTranslation } from "react-i18next";
import { debounce } from "lodash";
import { format } from "date-fns";

import fpAdjustmentGanttService from "@plannel/services/fp/fp-adjustment-gantt-service";
import fpCommonService from "@plannel/services/fp/fp-common-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import GanttActivitySearch from "@plannel/pages/factory-plan/common/gantt/GanttActivitySearch";
import GanttColumnFilter from "@plannel/pages/factory-plan/common/gantt/GanttColumnFilter";
import GanttFrame from "@plannel/pages/factory-plan/common/gantt/GanttFrame";
import GanttTooltip from "@plannel/pages/factory-plan/common/gantt/GanttTooltip";
import GanttViewControls from "@plannel/pages/factory-plan/common/gantt/GanttViewControls";
import AdjustDetailTab from "@plannel/pages/factory-plan/simulation/adjustment-gantt/AdjustDetailTab";
import ResourcePlanDetailTab from "@plannel/pages/factory-plan/report/resource-gantt/ResourcePlanDetailTab";
import PsiTab from "@plannel/pages/factory-plan/report/resource-gantt/PsiTab";
import {
  buildRowLayout, jcLinkGapsOf, markAbutment, OVERLAP_MODE
} from "@plannel/pages/factory-plan/common/gantt/ganttRowModel";
import {
  applyShrink,
  applySlide,
  attachStart,
  buildUnits,
  compactGroup,
  DROP_BLOCKED,
  DROP_FREE,
  DROP_PUSH,
  groupKeyOf,
  resizeGroupBoxes,
  resolveDrop,
  relaxBound,
  resolveResizeEdge,
  runClamped,
  MIN_DURATION_MS,
  RESIZE_LEFT,
  RESIZE_RIGHT,
  snapTs,
  SNAP_MS,
  unitsToBoxes
} from "@plannel/pages/factory-plan/common/gantt/adjustShrink";
import {
  buildJcIndex, reassignJc
} from "@plannel/pages/factory-plan/common/gantt/jcMaster";
import {
  applyDivideRules, attachNetDuration, clampResizeEdge, clampStartBeforeDowntime,
  divideMarksOf, normalizeDowntimes, pullRow, shiftDragStart
} from "@plannel/pages/factory-plan/common/gantt/divideRules";
import {
  drawBox,
  drawBoxLabel,
  drawChangedMark,
  drawDivideMarks,
  drawDowntimes,
  drawDragTip,
  drawDropBand,
  drawDropPlaceholder,
  drawFrozenZone,
  drawJcLink,
  drawOriginOutline,
  drawPushedRail,
  drawRowDivider,
  drawSelectionRing,
  drawTimelineGrid,
  drawWeekends
} from "@plannel/pages/factory-plan/common/gantt/ganttDraw";
import { buildGanttBound, buildTicks, buildTimeScale, buildWeekendRanges, toMs } from "@plannel/pages/factory-plan/common/gantt/timelineScales";
import { formatCodeName, formatQty } from "@plannel/pages/factory-plan/common/gantt/ganttFormat";
import {
  CTRL_GROUP_SX,
  DEFAULT_HEADER_UNIT,
  DROP_SEAM_PX,
  LANE_BAR_PADDING_Y,
  RESIZE_EDGE_PX,
  RESIZE_EDGE_RATIO,
  RESIZE_OUTER_PX,
  ROW_HEIGHT_DEFAULT,
  UI,
  WIDTH_ZOOM_MAX,
  WIDTH_ZOOM_MIN,
  WIDTH_ZOOM_STEP
} from "@plannel/pages/factory-plan/common/gantt/ganttConstants";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ConfirmDialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import ColorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — 자원 간트와 같은 결정(§A-3c).
// TODO(MIG-T2): [FP_ADJUST_GANTT] 조회조건 세팅 3종(버킷 목록/검색필드 표시/검색구간)의 테넌트별
//   커스터마이징 미이관.
//   old: setting FP_SRH_RANGE / search-option 조회로 planScope별 설정을 읽음
//   사유: 정적 구성으로 전환 — fp-report-screen-guide.md §A-3~§A-3d에서 결정된 것과 동일 규칙
const SEARCH_ZONE_DAYS = 9;

const PARAM_DATE_FORMAT = "yyyyMMdd";
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const SEARCH_FIELDS = [
  { key: "order", labelKey: "FP_WO_CD" },
  { key: "item", labelKey: "FP_ITEM" }
];

// 자원 간트와 같은 값(§7-1a) — 겹침은 한 트랙에 겹쳐 그린다(old `GanttRunErrorsShift:''`).
const TRACK_HEIGHT = 20;
const overlapMode = OVERLAP_MODE.OVERLAY;

// 버튼 이동 배수 — old는 `hourOptions`/`minuteOptions`/`timeUnits` 3개 입력으로 단위를 골랐지만,
// **1시간으로 통일**하고 field를 없앴다(2026-08-28 사용자 결정, §7-15e). ×2 버튼만 2배다.
const MOVE_STEP_X2 = 2;

const checkTrue = (value) => value === true || value === "true";

// old checkSearchActivity 이식 — 품목(코드/명칭) 부분일치 AND 작업주문 부분일치.
const matchActivity = (search, activity) => {
  if (!activity) return true;
  const item = String(search?.item ?? "").toUpperCase();
  const order = String(search?.order ?? "").toUpperCase();
  if (!item && !order) return true;

  const inventoryCd = String(activity.inventoryCd ?? "").toUpperCase();
  const inventoryNm = String(activity.inventoryNm ?? "").toUpperCase();
  const woCd = String(activity.woCd ?? "").toUpperCase();

  return (!item || inventoryCd.includes(item) || inventoryNm.includes(item)) && (!order || woCd.includes(order));
};

/**
 * `calendar` 문자열 → 비가동 밴드 구간.
 *
 * 서버 형식은 `"{주기}#MM/dd/yyyy HH:mm~MM/dd/yyyy HH:mm;"` 반복이다
 * (`AdjustmentGanttService.getResourceCalendarMap`). old는 이 값을 TreeGrid `GanttBackground`에
 * 그대로 넘겼고, new는 자원 간트와 같은 `drawDowntimes`로 그린다 — 두 화면의 비가동 표현이 갈리지
 * 않게 하기 위해서다.
 *
 * TODO(MIG-T2): [FP_ADJUST_GANTT] 캘린더 **반복 주기 전개** 미이관.
 *   old: 접두 주기 문자(d/w/m/y)를 TreeGrid가 해석해 구간을 반복 전개
 *   사유: 서버가 전개된 구간이 아니라 주기 문자만 내려주고, 전개 규칙(주 시작요일·월말 처리)이
 *         TreeGrid 내부 규약이라 재현 근거가 없다. 주기 없는 구간(cycleTpCd "1"/"O")은 그대로 그린다.
 *
 * **↑ 위 TODO는 2026-09-08 데이터 소스 전환으로 해소됐다.** 기준정보 `tb_fp_resource_calendar`
 * (자원별 1회씩 조회 = N+1, 자원 54개에 3,509 ms) 대신 **계획 결과 `tb_fp_resrc_downtime`
 * (벌크 1회)** 을 쓰면서 서버가 `calendar` 문자열이 아니라 `downtimes`/`divideYDowntimes`
 * **배열**을 준다. 계획 결과는 엔진이 이미 전개한 실제 구간이라 주기 개념 자체가 없고,
 * 활동 시각과 같은 `toMs` 경로를 타므로 옛 파서의 타임존 함정(서버 로컬 포맷을
 * `new Date(y,m,d,…)`로 해석해야 했던 것)도 사라졌다.
 */
const toRanges = (list) => (list ?? [])
  .map(downtime => ({ s: toMs(downtime.startTs), e: toMs(downtime.endTs) }))
  .filter(range => !Number.isNaN(range.s) && !Number.isNaN(range.e) && range.e > range.s);

/**
 * old `activityToRunBox` 이식 — 활동 배열 → 캔버스 박스 배열.
 * `startTs` 오름차순 정렬이 히트테스트·트랙 패킹·`buildUnits`의 전제다.
 *
 * `inBound`는 old `checkActivityInBound`가 정하던 박스 `Type:'Fixed'`(= 이동·리사이즈 불가)에 대응한다.
 * 활동이 축 구간 밖으로 걸쳐 있으면 편집을 잠근다 — 화면에 보이지 않는 부분이 같이 움직이기 때문이다.
 */
/** JC 행의 `prev_jc_time`/`next_jc_time`(분) → ms. 둘 중 실린 쪽을 쓴다(서버가 한쪽만 채운다). */
const jcTimeMs = (activity) => {
  const minutes = Number(activity?.prevJcTime) || Number(activity?.nextJcTime) || 0;
  return minutes > 0 ? minutes * 60 * 1000 : null;
};

const buildBoxes = (activities, bound) => (activities ?? [])
  .map(activity => {
    const s = toMs(activity.startTs);
    const e = toMs(activity.endTs);
    if (Number.isNaN(s)) return null;

    const jc = checkTrue(activity.jcYn);
    const end = Number.isNaN(e) ? s : e;
    // JC 시각은 본 활동에 딸려 있으므로 본 활동 기준으로 판정한다(old도 prevJc/nextJc 시각을 봤다).
    const outside = bound ? (s < bound.startTs || end > bound.endTs) : false;

    return {
      id: activity.uniqueId,
      s,
      e: end,
      type: jc ? "jc" : "box",
      jcKind: jc ? (String(activity.uniqueId ?? "").endsWith("_NEXTJC") ? "next" : "prev") : null,
      // **마스터에서 온 순수 JC 소요**(`jc_time`, 분 단위 — `ActivitySaveRunner:174 getAsMinutes()`).
      // 보정 때 JC 를 활동에서 다시 파생할 때 이 값을 쓴다 — 좌표에서 역산하면 휴무를 품은 `Y` 의
      // 부풀린 span 을 순수 소요로 착각한다(§7-41 0-b). 없으면 `placeJcOf` 가 span 으로 폴백한다.
      jcTime: jc ? jcTimeMs(activity) : null,
      color: activity.displayColor,
      text: activity.woCd,
      late: checkTrue(activity.lateYn),
      hasTool: false,
      inBound: !outside,        // false = 편집 잠금(old Type:'Fixed')
      changed: false,
      activity
    };
  })
  .filter(Boolean)
  .sort((a, b) => a.s - b.s);

// 정렬 직후 인접(맞닿음)을 표시한다 — `drawBox`가 맞닿은 쪽 코너를 각지게 그린다(§7-28).
// **여기가 맞는 자리다**: 인접은 시각(ms)의 성질이라 줌·행높이가 바뀌어도 다시 계산할 필요가 없다.
// `netMs`(순수 소요)는 **로드 시 원래 좌표에서** 한 번 붙인다 — 이동 후에 구하면 늘어난 span 을
// 순수로 오인해 길이가 안 줄어든다(`divideRules.attachNetDuration` 주석).
const buildLaneBoxes = (activities, bound, downtimes) =>
  markAbutment(attachNetDuration(buildBoxes(activities, bound), downtimes));

/**
 * 편집으로 박스의 시각이 바뀐 뒤 **인접을 다시 계산한다**(§7-28a). 보정 간트는 이동·리사이즈·슬라이드로
 * 작업이 붙었다 떨어졌다 하므로, 로드 시 한 번 계산한 `abutPrev/abutNext`는 곧바로 낡는다 —
 * 떼어냈는데 코너가 각진 채로 남거나, 붙였는데 노치가 그대로 보인다. 밀림(sweep)으로 시각이 바뀐
 * **이웃들도 같이** 영향을 받으므로 행 단위로 통째로 다시 돌린다.
 *
 * ⚠️ **얕은 복사를 먼저 한다.** `markAbutment`는 제자리 수정이고, 이동 시 원래 행에 남는 박스들은
 *   **undo 스냅샷과 같은 객체**다(`row.boxes.filter(...)`는 배열만 새로 만든다). 복사 없이 표시하면
 *   스냅샷의 플래그까지 편집 후 값으로 덮여 **undo가 틀린 코너 모양을 복원**한다.
 *   비용은 편집된 행 하나의 박스 수뿐이라 사용자 조작 1회에 무시할 만하다.
 */
const reflowBoxes = (boxes = []) => markAbutment(boxes.map(box => ({ ...box })));

/**
 * 규칙 적용 후 **이동 그룹의 끝시각** — 초록 자리표시·드래그 툴팁이 이 값을 써야 한다.
 * JC를 포함한 그룹 전체의 오른쪽 끝을 잡는다(강체 이동이라 그룹이 하나의 박스처럼 보인다).
 * 못 찾으면 `fallback`(규칙 적용 전 길이)으로 떨어진다.
 */
const ruledEndOf = (boxes, unitKey, fallback) => {
  let end = null;
  boxes.forEach(box => {
    if (groupKeyOf(box.id) !== unitKey) return;
    if (end === null || box.e > end) end = box.e;
  });
  return end ?? fallback;
};

/**
 * 규칙 적용 후 그룹의 **시작시각**. 고스트가 실제 착지점과 어긋나지 않게 쓴다.
 *
 * `applyDivideRules`가 시작시각도 손대므로(앞 박스의 늘어난 끝에 밀리거나 휴무 밖으로 밀린다)
 * 요청한 시작(`sweep.start`)을 그대로 그리면 다시 "저기로 안 갔다"가 된다.
 */
const ruledStartOf = (boxes, unitKey, fallback) => {
  let start = null;
  boxes.forEach(box => {
    if (groupKeyOf(box.id) !== unitKey) return;
    if (start === null || box.s < start) start = box.s;
  });
  return start ?? fallback;
};

// old `common/utils/gantt.js:530 getGanttRowData` — 선택 자원 행의 활동 목록("생산 계획 상세" 탭).
// 자원 간트와 같은 규칙: 준비시간(jcYn)은 뺀다.
const buildResourcePlanRows = (row) => (row?.boxes ?? [])
  .filter(box => !checkTrue(box.activity?.jcYn))
  .map(box => ({
    ...box.activity,
    // 편집으로 바뀐 값이 탭에도 그대로 보여야 한다(§6-6) — 박스가 진실 소스다.
    startTs: new Date(box.s),
    endTs: new Date(box.e),
    uniqueId: box.id,
    plantCd: row.plantCd,
    plantNm: row.plantNm,
    resourceCd: row.resourceCd,
    resourceNm: row.resourceNm,
    lateYn: box.late
  }));

/**
 * **실제로 좌표가 바뀐** unit 키 집합. 변경 표식(`changed`)과 변경 건수의 근거다.
 *
 * ⚠️ **스윕이 보고한 `pushedKeys` 를 그대로 쓰면 안 된다.** 스윕은 **규칙 적용 전 길이**로 이웃을
 * 미는데, 기준 박스가 휴무를 품어 span 이 부풀어 있으면 멀리 있던 박스까지 밀었다고 보고한다.
 * 그다음 규칙이 기준 박스를 순수 소요로 줄이면 그 박스들은 `homeS` 까지 **그대로 되돌아온다**
 * (§7-29c). 결과적으로 **좌표가 하나도 안 바뀐 박스에 변경 표식이 붙고 변경 건수가 부풀었다**
 * (2026-09-10 신고: "영향을 안 받을 작업들에 파란 표식이 붙는다").
 *
 * 최종 좌표를 `homeS`/`homeE`(스윕 전 자리, `unitsToBoxes` 가 얹는다)와 직접 비교한다.
 * 이동 대상(`unitKey`)은 **항상 포함**한다 — 행간 이동은 시각이 같아도 자원이 바뀐 변경이다.
 *
 * ⚠️ **JC 띠는 근거로 치지 않는다**(§7-55, 2026-09-16 신고 — 같은 부류의 재발이다).
 * JC 는 파생값이라 **활동이 가만히 있어도 좌표가 바뀔 수 있다**. 특히 `buildUnits` 가 그룹을
 * 압축해 재고(§7-51) `unitsToBoxes` 가 **그 압축 좌표를** `homeS` 로 기록하는데, 규칙이 JC 를
 * 활동에서 다시 파생하면 원래(떨어진) 자리로 돌아간다 — `home ≠ 최종` 이 되어 **아무도 안
 * 움직였는데 밀림 예고(주황)가 뜨고 변경 건수까지 부푼다.**
 * 활동이 실제로 밀렸다면 **그 활동 박스**가 잡으므로 판정력은 잃지 않는다.
 */
const movedKeysOf = (boxes = [], unitKey = null) => {
  const keys = new Set(unitKey ? [unitKey] : []);
  boxes.forEach(box => {
    if (box.type === "jc") return;
    const homeS = Number.isFinite(box.homeS) ? box.homeS : box.s;
    const homeE = Number.isFinite(box.homeE) ? box.homeE : box.e;
    if (box.s !== homeS || box.e !== homeE) keys.add(groupKeyOf(box.id));
  });
  return keys;
};

/**
 * **손댄 작업의 서로 다른 개수.** 상단 "변경 N" 배지의 근거다(§7-56).
 *
 * ⚠️ **누적 카운터를 쓰지 않는다.** 예전에는 조작할 때마다 `count + movedKeys.size` 로 더했는데,
 * 그러면 ① 같은 작업을 세 번 옮기면 3 이 되고(distinct 가 아니다) ② 되돌리기가 연쇄 밀기 수를
 * 모른 채 **무조건 1 씩** 뺐다(주석이 스스로 "정확히 빼기 어렵다"고 적고 있었다).
 * 무엇보다 **바에 붙는 파란 표식과 배지의 출처가 달라** 표식 3 개에 배지 7 이 될 수 있었다.
 *
 * `changed` 플래그(`syncActivity` 가 박는다)는 **되돌리기가 박스 스냅샷째 복원**하므로 저절로
 * 정확하고, 재조회하면 `buildBoxes` 가 `false` 로 새로 만든다 — 파생값으로 두면 리셋이 공짜다.
 *
 * JC 띠는 제외한다(§7-55 와 같은 이유 — 파생값이라 주인이 안 움직여도 좌표가 바뀐다).
 * 의미는 **"사용자가 건드린 작업"** 이다 — 제자리로 되돌려 놔도 1 건으로 센다(2026-09-16 결정).
 * 좌표 기준("원래와 실제로 달라진 것")으로 올리려면 조회 시점 좌표를 따로 들고 있어야 한다.
 */
const changedCountOf = (rows = []) => {
  const keys = new Set();
  rows.forEach(row => (row.boxes ?? []).forEach(box => {
    if (box.changed && box.type !== "jc") keys.add(groupKeyOf(box.id));
  }));
  return keys.size;
};

/**
 * old `setActivityData` 이식 — 이동 결과를 **박스의 속성 사본에도 되쓴다.**
 * 이걸 빼먹으면 바는 옮겨졌는데 툴팁·상세탭·PSI 조회는 옛 값을 쓰는 상태가 된다(§6-6의 핵심).
 */
const syncActivity = (box, row) => ({
  ...box,
  changed: true,
  activity: {
    ...box.activity,
    startTs: new Date(box.s),
    endTs: new Date(box.e),
    resourceCd: row.resourceCd,
    resourceNm: row.resourceNm,
    plantCd: row.plantCd,
    plantNm: row.plantNm
  }
});

const DETAIL_TABS = [
  { key: "adjust", labelKey: "FP_PLAN_DETAIL_ADJUST" },
  { key: "plan", labelKey: "FP_RESOURCE_PLAN_DETAILS" },
  { key: "psi", labelKey: "PSI" }
];
// 하단 분할 치수는 자원 간트(§7-14)와 같은 값을 쓴다 — 두 화면이 나란히 쓰이므로 다르면 어색하다.
const DETAIL_TAB_BAR_HEIGHT = 38;
const DETAIL_OPEN_HEIGHT = 340;
const DETAIL_MAX_OPEN = 720;
const DETAIL_GUTTER = 10;
const DETAIL_GUTTER_MARGIN = 4;
const DETAIL_CONTENT_GAP = 6;
const DETAIL_COLLAPSE_THRESHOLD = DETAIL_TAB_BAR_HEIGHT + 45;

/**
 * 목표시각을 **휴무 밖으로 클램프**한다 — 밴드 안이면 `origin` 기준 격자의 밴드 직전 칸으로.
 * `origin` 이 없으면(격자 기준이 없으면) 그대로 둔다. 규칙은 `shiftDragStart` 주석 참고.
 */
const workingStart = (origin, target, downtimes) => (
  Number.isFinite(origin) ? shiftDragStart(origin, target - origin, downtimes, SNAP_MS) : target
);

/**
 * 스윕(`applyShrink`/`applySlide`) + 분할타입 규칙을 **한 덩어리의 제약**으로 보고,
 * 걸리면 **갈 수 있는 데까지만** 옮긴다(2026-09-08 사용자 요청 — 튕겨서 원래 자리로 돌아가지 말 것).
 *
 * 두 제약을 같이 묶는 이유: 따로 검사하면 스윕은 통과했는데 규칙이 막는 위치에서 다시 튕긴다.
 * 하나의 판정 함수로 만들어 `runClamped`에 넘기면 **둘 다 만족하는 가장 먼 지점**이 나온다.
 *
 * @returns `{ result, boxes, start, clamped }` — `result.units`는 스윕 결과, `boxes`는 규칙까지 적용된 박스.
 *          현재 자리조차 불가하면 `{ failed: true, blockedBox }`(호출부가 메시지를 고른다).
 */
/**
 * 드래그 1회 · 한 행에 대한 클램프 캐시. **행이 바뀌면 버린다** — 좌클릭 이동은 커서가 행을 넘나들어
 * 같은 드래그 안에서도 대상 행이 달라진다. 캐시는 "행이 고정"이라는 전제 위에서만 유효하다.
 */
/**
 * **인접이 바뀐 활동 키** — 옮긴 unit 과 그 양옆. JC 를 다시 정할 대상이다(§7-59).
 *
 * ⚠️ 행 전체를 다시 계산하지 않는다. 조회 직후 값은 백엔드(엔진 저장값)가 정본이고, 전체를
 * 돌리면 *조회만 해도* 화면이 바뀌어서 프런트 해석과 엔진의 차이가 "데이터가 변했다"로 보인다.
 */
const jcDirtyKeysOf = (boxes, unitKey) => {
  const keys = new Set();
  if (!unitKey) return keys;

  const mains = boxes.filter(box => box.type !== "jc").slice().sort((a, b) => a.s - b.s);
  const at = mains.findIndex(box => groupKeyOf(box.id) === unitKey);
  if (at === -1) return keys;

  keys.add(unitKey);
  // 양옆도 담는다 — 내 prev JC 는 앞 이웃과의 쌍이, 앞 이웃의 next JC 는 나와의 쌍이 정한다.
  if (mains[at - 1]) keys.add(groupKeyOf(mains[at - 1].id));
  if (mains[at + 1]) keys.add(groupKeyOf(mains[at + 1].id));
  return keys;
};

const clampMemoOf = (drag, key) => {
  if (!drag) return null;
  if (drag.clampMemoKey !== key) {
    drag.clampMemoKey = key;
    drag.clampMemo = {};
  }
  return drag.clampMemo;
};

const sweepClamped = ({ sweep, downtimes, bound, from, wanted, unitKey, memo = null }) => {
  // 기준 박스 — 이걸 축으로 앞쪽은 뒤로, 뒤쪽은 앞으로 채운다(`applyDivideRules` 주석).
  const isAnchor = unitKey ? (box) => groupKeyOf(box.id) === unitKey : null;
  let blockedBox = null;

  const ruleOf = (start) => {
    const result = sweep(start);
    if (!result) return null;                       // 계획 구간 이탈(규칙 **전** 길이 기준)
    // `unitKey` 를 넘겨 **끌고 있는 그룹에는 `homeS`/`homeE` 를 안 붙인다**(§7-41).
    // `groupKeyOf` 를 넘겨 **JC 를 활동의 파생값으로** 처리한다(§7-43). 안 넘기면 JC 가 독립
    // 박스로 훑여서 왼쪽 슬라이드 때 활동만 제자리로 되밀리고 JC 는 찢어진다(2026-09-15 신고).
    const ruled = applyDivideRules(
      unitsToBoxes(result.units, unitKey), downtimes, isAnchor, groupKeyOf
    );
    if (ruled.blocked) {
      blockedBox = ruled.box;                       // N 타입이 휴무에 걸림
      return null;
    }
    return { ...result, boxes: ruled.boxes };
  };

  // 기준점(현재 자리)의 **규칙 적용 후** 결과로 경계를 넓힌다. 이렇게 잡아야 기준점이 반드시
  // 통과해서 클램프의 이분 탐색이 성립한다 — 이미 구간 밖으로 나가 있는 만큼은 위반이 아니다.
  // 드래그 중에는 행도 `from` 도 안 변하므로 **한 번만 구하면 된다**(`memo`, `runClamped` 주석).
  let limit;
  if (memo && memo.limitFrom === from) {
    limit = memo.limit;
  } else {
    limit = relaxBound(bound, ruleOf(from)?.boxes ?? []);
    if (memo) Object.assign(memo, { limitFrom: from, limit });
  }

  const clamped = runClamped(start => {
    const out = ruleOf(start);
    if (!out) return null;

    // ⚠️ 경계를 **규칙 적용 후에 다시** 본다. 스윕(`applyShrink`/`applySlide`) 안의 검사는
    //    **규칙 전 길이**라, Y가 휴무만큼 늘어난 분이 검사를 통과한 뒤에 붙는다 — 그만큼이
    //    그대로 구간 밖으로 빠져나갔다(2026-09-09 사용자 신고: "밀면 구간 밖으로 밀어진다").
    //    휴무가 없는 행은 규칙이 길이를 안 바꿔서 멀쩡했고, 그래서 행마다 달라 보였다.
    if (limit && out.boxes.length > 0) {
      const first = Math.min(...out.boxes.map(box => box.s));
      const last = Math.max(...out.boxes.map(box => box.e));
      if (first < limit.startTs || last > limit.endTs) return null;
    }
    return out;
  }, from, wanted, memo);

  if (!clamped) return { failed: true, blockedBox };
  return { ...clamped.result, start: clamped.start, clamped: clamped.clamped };
};

/**
 * 리사이즈 후 재배치 — **끄는 방향으로 민다.** 미리보기와 커밋이 같은 결과를 내도록 한 곳에 둔다.
 *
 * 우측은 뒤쪽만 밀면 되므로 `applyShrink`, **좌측은 앞쪽을 밀어야 해서 양방향인 `applySlide`**를 쓴다
 * (2026-09-08 — 좌측이 선행 박스에서 멈추던 걸 우측과 맞춰 달라는 요청). `applyShrink`는 뒤로만
 * 밀 수 있어서 좌측에 쓰면 리사이즈 대상 자체가 오른쪽으로 되밀려 조작이 취소된다.
 *
 * ⚠️ `applySlide`는 **입력 순서를 지켜야 할 순서로 본다.** `buildUnits`는 시작시각으로 정렬하는데,
 * 좌측으로 늘린 박스가 선행보다 앞서면 정렬 결과가 뒤집혀 앞쪽 밀기가 성립하지 않는다.
 * 그래서 **리사이즈 전 순서**(`orderIndex`)로 되돌려 넘긴다.
 *
 * ⚠️ `orderIndex` 는 `Map<key, 순번>` 이다 — **배열 + `indexOf` 를 쓰면 안 된다.** 비교자 안에서
 * 선형 탐색을 하게 되어 `O(N² log N)` 이 되고, 이 함수는 클램프 이분 탐색 때문에 마우스무브 한 번에
 * 25회쯤 불린다. 600박스 행에서 프레임당 14ms → 4ms 차이였다(2026-09-10 신고: "특정 행에서
 * 왼쪽 리사이즈만 느리다"). 우측(`applyShrink`)은 이 정렬이 없어서 영향을 안 받았다.
 */
const resizeSweep = ({ restBoxes, resized, unitKey, mode, orderIndex }) => {
  const units = buildUnits([...restBoxes, ...resized]);
  const moving = units.find(unit => unit.key === unitKey);
  if (!moving) return null;

  // 경계는 여기서 보지 않는다 — `sweepClamped` 가 규칙 적용 후에 본다(위 주석과 같은 이유).
  const swept = mode === RESIZE_LEFT
    ? applySlide({
      units: [...units].sort((a, b) => orderIndex.get(a.key) - orderIndex.get(b.key)),
      movingKey: unitKey,
      newStart: moving.s
    })
    : applyShrink({ units, movingKey: unitKey, newStart: moving.s });

  return swept && { ...swept, resizedBoxes: resized, mainStart: moving.s, mainEnd: moving.e };
};

const AdjustmentGantt = ({ t, viewName }) => {
  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  const [resourceState, setResourceState] = useState({ selected: [], options: [] });
  const [resourceDraft, setResourceDraft] = useState(null);

  const [search, setSearch] = useState({ order: "", item: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [loading, setLoading] = useState(false);
  const [rawRows, setRawRows] = useState([]);
  const [frozenZone, setFrozenZone] = useState(null);
  const [bound, setBound] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [altHighlight, setAltHighlight] = useState(() => new Set());

  const [headerUnit, setHeaderUnit] = useState(DEFAULT_HEADER_UNIT);
  const [widthZoom, setWidthZoom] = useState(1);
  const [rowHeight, setRowHeight] = useState(ROW_HEIGHT_DEFAULT);

  const [resourceFilter, setResourceFilter] = useState(() => new Set());
  const [filterAnchor, setFilterAnchor] = useState(null);

  const [detailTab, setDetailTab] = useState(DETAIL_TABS[0].key);
  const [detailHeight, setDetailHeight] = useState(DETAIL_TAB_BAR_HEIGHT);
  const detailDragRef = useRef(null);
  const detailCollapsed = detailHeight < DETAIL_COLLAPSE_THRESHOLD;

  // ── 편집 상태 ─────────────────────────────────────────────────────────────
  // 변경 건수 — 저장 경로가 없는 화면이라(§6-1) "무엇을 건드렸는지"가 화면에 남아야 한다.
  // **상태가 아니라 파생값이다**(§7-56) — 근거는 바에 붙는 파란 표식과 **같은** `changed` 플래그다.
  // Undo는 **행 단위 스냅샷**이다 — 연쇄 밀기가 한 번에 여러 박스를 건드리므로 박스 단위로는 못 되돌린다.
  const undoRef = useRef([]);

  const altResourcesRef = useRef({});
  // JC 기준정보 인덱스(§7-59) — 공정 순서가 바뀐 쌍의 JC 를 여기서 다시 정한다.
  // ⚠️ `altResourcesRef` 와 같은 이유로 ref 다: 조회 1회에 한 번 채우고 커밋 경로에서만 읽는다.
  // **planScope 에만 종속**이라 버전만 바꿔 재조회할 때도 유효하지만, 조회 토큰으로 같이 갱신한다.
  const jcIndexRef = useRef(null);

  /**
   * 인접이 바뀐 활동들의 JC 를 기준정보로 다시 정하고, 새로 생긴 JC 를 규칙으로 앉힌다(§7-59).
   *
   * ⚠️ **여기서만 JC 박스가 생기고 사라진다.** 나머지 경로는 좌표만 옮긴다.
   * 새 JC 가 생기면 그룹 길이가 바뀌므로 `applyDivideRules` 를 한 번 더 태워야 한다 —
   * 안 그러면 새 JC 가 휴무 위에 그대로 앉는다.
   */
  const reassignRowJc = useCallback((boxes, row, keys) => {
    const index = jcIndexRef.current;
    if (!index || !row?.resourceCd || keys?.size === 0) return boxes;

    const out = reassignJc(boxes, {
      keys, index, resourceCd: row.resourceCd, ownerKeyOf: groupKeyOf
    });
    if (out.boxes === boxes) return boxes;

    const ruled = applyDivideRules(out.boxes, row.downtimes, null, groupKeyOf);
    // 규칙이 막으면(새 JC 때문에 `N` 이 안 들어감) **재계산 전 배치를 쓴다** — 이동 자체를
    // 되돌리는 것보다 낫다. 좌표는 이미 합법이고 JC 만 예전 값인 상태다.
    return ruled.blocked ? boxes : ruled.boxes;
  }, []);
  const frameApiRef = useRef(null);
  const boxRectsRef = useRef(new Map());
  const selectedRectRef = useRef(null);
  const selectedJcRectsRef = useRef([]);
  const hoverTimerRef = useRef(0);
  const leftTipKeyRef = useRef(null);
  const latestReqRef = useRef(0);
  const latestResourceReqRef = useRef(0);

  // 드래그는 **ref로만** 돈다 — mousemove마다 setState하면 리렌더 + 툴팁이 붙어 드래그가 끊긴다(§6-6).
  const dragRef = useRef(null);

  const searchStateRef = useRef({ versionState, periodState, resourceState });
  searchStateRef.current = { versionState, periodState, resourceState };
  // 편집 계산은 최신 행을 즉시 읽어야 한다(드래그 중 클로저에 갇힌 값을 쓰면 한 박자 늦는다).
  const rawRowsRef = useRef(rawRows);
  rawRowsRef.current = rawRows;
  const boundRef = useRef(bound);
  boundRef.current = bound;
  // 드래그 핸들러는 의존성 없는 `useCallback`이라(매 mousemove마다 새로 만들어지면 안 된다)
  // 스케일도 ref로 읽는다 — 픽셀 허용치를 시각 단위로 환산할 때 필요하다.
  const scaleRef = useRef(null);

  const changedCount = useMemo(() => changedCountOf(rawRows), [rawRows]);

  const weekdayLabels = useMemo(() => WEEKDAY_KEYS.map(key => t(key)), [t]);
  const searchActive = Boolean(search.order || search.item);
  const matchFn = useCallback((activity) => matchActivity(search, activity), [search]);
  const [matchIndex, setMatchIndex] = useState(0);

  const scale = useMemo(
    () => (bound ? buildTimeScale({ startTs: bound.startTs, endTs: bound.endTs, unit: headerUnit, zoom: widthZoom }) : null),
    [bound, headerUnit, widthZoom]
  );
  scaleRef.current = scale;
  const ticks = useMemo(() => buildTicks(scale, { weekdayLabels }), [scale, weekdayLabels]);
  const weekendRanges = useMemo(() => buildWeekendRanges(scale), [scale]);

  const resourceFilterOptions = useMemo(() => {
    const seen = new Set();
    rawRows.forEach(row => seen.add(row.resourceCd));
    return [...seen].map(value => ({ value, label: value }));
  }, [rawRows]);

  useEffect(() => {
    setResourceFilter(prev => (prev.size === 0 ? prev : new Set()));
  }, [rawRows]);

  const filteredRows = useMemo(
    () => (resourceFilter.size === 0 ? rawRows : rawRows.filter(row => resourceFilter.has(row.resourceCd))),
    [rawRows, resourceFilter]
  );

  useEffect(() => {
    frameApiRef.current?.scrollTo({ top: 0 });
  }, [resourceFilter]);

  const layout = useMemo(
    () => buildRowLayout(filteredRows, { overlapMode, baseRowHeight: rowHeight, trackHeight: TRACK_HEIGHT }),
    [filteredRows, rowHeight]
  );

  const matches = useMemo(() => {
    if (!searchActive) return [];

    const hits = [];
    layout.rows.forEach(row => (row.boxes ?? []).forEach(box => {
      if (box.type !== "jc" && matchActivity(search, box.activity)) hits.push({ box, row });
    }));
    return hits.sort((a, b) => a.box.s - b.box.s);
  }, [layout, search, searchActive]);

  const matchedResources = useMemo(() => new Set(matches.map(hit => hit.row.resourceCd)), [matches]);
  const dimming = searchActive && matches.length > 0;

  // 선택 박스/행 — 편집 결과가 상세탭·툴바 활성에 바로 반영되도록 **행에서 파생**시킨다(§6-6).
  const selected = useMemo(() => {
    if (!selectedId) return { box: null, row: null };
    for (const row of rawRows) {
      const box = (row.boxes ?? []).find(candidate => candidate.id === selectedId);
      if (box) return { box, row };
    }
    return { box: null, row: null };
  }, [rawRows, selectedId]);

  const resourcePlanRows = useMemo(() => {
    if (!selectedRowKey) return [];
    const row = rawRows.find(candidate => candidate.rowKey === selectedRowKey);
    return row ? buildResourcePlanRows(row) : [];
  }, [rawRows, selectedRowKey]);

  const selectedAltResources = useMemo(
    () => altResourcesRef.current?.[selected.box?.activity?.routeCd] ?? [],
    // altResourcesRef는 ref라 의존성에 못 넣는다 — 선택이 바뀔 때만 다시 읽으면 충분하다
    // (대체자원 목록은 조회 직후 1회만 채워지고 그 뒤 바뀌지 않는다).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected.box]
  );

  const columns = useMemo(() => [
    { key: "plantNm", label: t("FP_PLANT"), width: 140, spanRows: true, valueGetter: (row) => row.plantNm ?? row.plantCd ?? "" },
    {
      key: "resourceCd", label: t("FP_RESOURCE"), width: 200, valueGetter: (row) => row.resourceCd ?? "",
      filterable: true,
      filterActive: resourceFilter.size > 0,
      // 대체자원 강조는 이 화면에서 **강조 이상의 의미**를 갖는다 — 드래그로 옮길 수 있는 행이 곧
      // 이 행들이다(old `OnGanttRunDrop`의 `resourceNmClass` 가드와 같은 출처).
      cellStyle: (row) => ({
        ...(altHighlight.has(row.resourceCd) ? { backgroundColor: UI.altResourceBg, fontWeight: 700 } : null),
        ...(matchedResources.has(row.resourceCd) ? { boxShadow: `inset 3px 0 0 ${UI.headerAccent}` } : null)
      })
    }
  ], [t, altHighlight, matchedResources, resourceFilter]);

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const notify = useCallback((content, severity = snackBarMessageColor.inputError) => {
    setSnackInfo({ open: true, severity, content });
  }, []);

  const closeTooltip = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    setTooltip(null);
  }, []);

  /**
   * 바 툴팁 — **전부 로컬 값**이다(old `OnGanttTip`과 동일, 서버 호출 없음).
   * 항목·순서는 old와 같고 위계만 자원 간트의 3단 구조에 맞춘다.
   */
  const openTooltip = useCallback((box, row, clientX, clientY) => {
    const activity = box?.activity;
    if (!activity) return;

    setTooltip({
      x: clientX,
      y: clientY,
      kind: "activity",
      captionKey: "FP_ACTIVITY",
      code: formatCodeName(activity.itemCd, activity.itemNm),
      groups: [
        { rows: [{ labelKey: "FP_QTY", value: formatQty(activity.qty, activity.itemUom) }] },
        {
          rows: [
            { labelKey: "FP_RESOURCE", value: formatCodeName(row?.resourceCd, row?.resourceNm) },
            { labelKey: "FP_START_TS", value: dateUtils.formatDateTime(new Date(box.s)) },
            { labelKey: "FP_END_TS", value: dateUtils.formatDateTime(new Date(box.e)) }
          ]
        },
        {
          rows: [
            { labelKey: "FP_WO_CD", value: activity.woCd },
            { labelKey: "FP_INVENTORY_NM", value: formatCodeName(activity.inventoryCd, activity.inventoryNm), wrap: true },
            { labelKey: "FP_REQUEST_QTY", value: formatQty(activity.shptQty, activity.itemUom) },
            {
              labelKey: "FP_DUE_DT",
              value: activity.dueDate ? dateUtils.formatDateTime(activity.dueDate) : "",
              ...(box.late ? { tone: "warn" } : null)
            }
          ]
        }
      ]
    });
  }, []);

  // 바 선택 — old OnGanttRunSelect: JC가 아닌 바를 고르면 그 공정의 대체자원 행을 강조한다.
  const selectBox = useCallback((box, row) => {
    // **JC 띠를 골라도 주인 작업이 선택된다**(2026-09-15 사용자 지시). 선택 케이싱이 이미
    // JC+작업을 묶어 두르므로(§7-40 D) 선택 주체도 그룹이어야 한다.
    //
    // ⚠️ 옛 코드는 JC 를 `null` 로 **지웠다.** 그런데 `handleDragStart` 는 같은 클릭의
    // pointerdown 에서 이미 주인(`main`)을 선택해 둔다 — 그래서 **링이 떴다가 바로 사라졌다**
    // (신고: "나타났다가 바로 사라져"). 두 경로가 같은 규칙을 쓰면 그 어긋남이 사라진다.
    const target = box?.type === "jc"
      ? (row?.boxes ?? []).find(candidate => candidate.id === groupKeyOf(box.id)) ?? null
      : (box ?? null);

    setSelectedId(target ? target.id : null);
    setSelectedRowKey(target ? (row?.rowKey ?? null) : null);

    const allowed = new Set();
    if (target) {
      (altResourcesRef.current?.[target.activity?.routeCd] ?? []).forEach(alt => allowed.add(alt.resourceCd));
    }
    setAltHighlight(allowed);
  }, []);

  const findBoxAt = useCallback((row, ts) => {
    if (!row?.boxes?.length || ts == null) return null;

    for (let i = row.boxes.length - 1; i >= 0; i -= 1) {
      const box = row.boxes[i];
      if (ts >= box.s && ts <= box.e) return box;
    }
    return null;
  }, []);

  // 리사이즈 손잡이 판정. **커서 모양과 드래그 시작이 같은 함수를 쓴다** — 계산을 복제하면
  // "커서는 리사이즈인데 잡으면 이동"이 되는 어긋남이 생긴다(트리 ▶ 히트 판정에서 이미 겪은 부류, §7-1b S4).
  const resizable = useCallback((box) => (
    // JC 띠는 리사이즈 대상이 아니다 — old 박스 `Type:'Solid'`(이동만 가능, 리사이즈 불가).
    Boolean(box) && Boolean(scale) && box.type !== "jc" && box.inBound !== false
  ), [scale]);

  /**
   * ① **바 안쪽** 손잡이 — 폭에 비례하고 **최소 폭 임계값이 없다**(§7-15g).
   *
   * 옛 구현은 "폭 < 18px이면 엣지 존을 끈다"였는데, 그 18px이 시간으로는 기본 버킷에서 1시간 48분,
   * 주 버킷에서 13시간 45분이라 **좁은 작업은 hover해도 손잡이가 안 잡혔다**(사용자 신고).
   * 비례로 두면 폭과 무관하게 잡히고 **이동 영역이 항상 40% 남는다** — 임계값이 있던 이유가 해소된다.
   */
  const resizeEdgeAt = useCallback((box, ts) => {
    if (!resizable(box)) return null;

    // px 허용치와 폭 비례 허용치 중 **작은 쪽**. 둘 다 시각 단위로 환산해 비교한다.
    const tol = Math.min(RESIZE_EDGE_PX / scale.pxPerMs, (box.e - box.s) * RESIZE_EDGE_RATIO);
    if (ts - box.s <= tol) return RESIZE_LEFT;
    if (box.e - ts <= tol) return RESIZE_RIGHT;
    return null;
  }, [scale, resizable]);

  /**
   * ② **경계 바깥** 손잡이 — 바 끝이 화면에서 1~2px일 때 유일한 조준 수단이다.
   * `findBoxAt`은 박스 **안**만 보므로 이건 따로 찾아야 한다. 반환은 `{ box, edge }`다.
   *
   * ⚠️ **이웃(또는 JC) 몸통 위면 포기한다** — 연속 배치(선행 종료 == 후속 시작)에서는 "바깥"이 곧
   * 이웃의 "안쪽"이라, 그대로 두면 한 경계에서 판정이 **4중으로 겹친다**(A 우측 리사이즈 / B 좌측
   * 리사이즈 / 각자의 안쪽 손잡이). 이 한 줄이 그걸 막고, 경계에서는 "왼쪽을 가리키면 A의 우측 끝,
   * 오른쪽을 가리키면 B의 좌측 끝"으로 자연히 갈린다.
   */
  const outerResizeEdgeAt = useCallback((row, ts) => {
    if (!scale || ts == null || !row?.boxes?.length) return null;
    if (findBoxAt(row, ts)) return null;

    const tol = RESIZE_OUTER_PX / scale.pxPerMs;
    for (let i = row.boxes.length - 1; i >= 0; i -= 1) {
      const box = row.boxes[i];
      if (!resizable(box)) continue;
      if (ts < box.s && box.s - ts <= tol) return { box, edge: RESIZE_LEFT };
      if (ts > box.e && ts - box.e <= tol) return { box, edge: RESIZE_RIGHT };
    }
    return null;
  }, [scale, resizable, findBoxAt]);

  // ── 편집 코어 ─────────────────────────────────────────────────────────────
  // 이동은 전부 이 함수 하나를 통과한다(드래그 / 버튼 / 상세탭 "적용") — 규칙이 세 곳으로 갈리면
  // 같은 이동이 경로에 따라 다르게 동작한다.
  //
  // ⚠️ **`applied`(미리보기 결과)가 있으면 다시 계산하지 않는다.**
  // 옛 구현은 미리보기가 낸 **시작시각 하나**(`preview.start`)만 받아 전부 다시 돌렸다. 그런데 그
  // 값은 제약·규칙·클램프를 모두 거친 **파생값**이라, 그걸 다시 입력으로 넣으면 같은 자리가 나오지
  // 않는다 — 특히 앞 박스가 휴무에서 늘어나 이동 대상을 되밀어낸 경우, 되밀린 위치를 다시 요청하니
  // 아무것도 안 움직인 결과가 나온다(2026-09-09 사용자 신고: "마우스를 놓으면 원래대로 돌아간다").
  // **미리보기가 곧 확정이다** — 화면에 보여준 배치를 그대로 커밋한다.
  const commitMove = useCallback(({ fromRowKey, toRowKey, unitKey, newStart, applied }) => {
    const rows = rawRowsRef.current;
    const fromIndex = rows.findIndex(row => row.rowKey === fromRowKey);
    const toIndex = rows.findIndex(row => row.rowKey === toRowKey);
    if (fromIndex === -1 || toIndex === -1) return false;

    const fromRow = rows[fromIndex];
    const toRow = rows[toIndex];
    const sameRow = fromIndex === toIndex;

    const movingBoxes = fromRow.boxes.filter(box => groupKeyOf(box.id) === unitKey);
    if (movingBoxes.length === 0) return false;

    // 대상 행의 unit 목록 = (같은 행이면) 그대로, (다른 행이면) 대상 행 + 옮겨온 unit.
    const targetBoxes = sameRow ? toRow.boxes : [...toRow.boxes, ...movingBoxes];
    const units = buildUnits(targetBoxes);

    // 드래그로 왔으면 **미리보기가 계산해 둔 결과를 그대로 쓴다**(§아래 주석). 버튼 이동처럼
    // 미리보기를 거치지 않는 진입점만 여기서 직접 계산한다.
    const fromTs = units.find(unit => unit.key === unitKey)?.s ?? newStart;
    const result = applied ?? sweepClamped({
      // ⚠️ **스윕 안에서는 경계를 보지 않는다**(`bound: null`). 스윕은 규칙 적용 **전** 길이로
      // 이웃을 밀기 때문에 중간 형상이 최종보다 훨씬 극단적이다 — 휴무를 품던 앞 작업을 통째로
      // 밀어내는 식이라, 규칙이 정리하면 멀쩡할 배치까지 여기서 거부됐다(2026-09-09 신고:
      // "밴드 끝까지만 가고 더는 안 밀린다"). 경계는 `sweepClamped` 가 **규칙 적용 후**에 본다.
      sweep: start => applyShrink({ units, movingKey: unitKey, newStart: start }),
      downtimes: toRow.downtimes,
      unitKey,
      bound: relaxBound(boundRef.current, units),
      from: fromTs,
      wanted: workingStart(fromTs, newStart, toRow.downtimes)
    });

    if (result.failed) {
      notify(result.blockedBox
        ? t("FP_MSG_CANNOT_MOVE_TO_DOWNTIME", { wo: result.blockedBox?.activity?.woCd ?? "" })
        : t("FP_MSG_OUT_PLAN_BOUND"));
      return false;
    }

    // 갈 수 있는 데까지 물렸는데 **한 칸도 못 움직였다** — 왜 안 움직이는지 알려준다(2026-09-11 요청).
    // ⚠️ `clamped` 만 보면 "20h 끌었는데 18h 갔다"에도 뜨는데, 그건 화면으로 이미 보이는 사실이라
    // 소음이다. **전혀 안 움직인 경우만** 설명이 필요하다.
    // 행이 바뀌면 시각이 같아도 **옮겨진 것**이라 제외한다.
    if (sameRow && result.clamped && result.start === fromTs) {
      notify(t("FP_MSG_WARN_MOVE_ANYMORE"));
      return false;
    }

    // Undo 스냅샷 — 두 행 전체를 통째로 기억한다(연쇄 밀기가 여러 박스를 건드린다).
    undoRef.current.push({
      rows: [
        { rowKey: fromRow.rowKey, boxes: fromRow.boxes },
        ...(sameRow ? [] : [{ rowKey: toRow.rowKey, boxes: toRow.boxes }])
      ]
    });

    const movedKeys = movedKeysOf(result.boxes, unitKey);
    const nextBoxes = result.boxes
      // 이동/밀림이 있었던 박스만 속성 사본을 되쓴다(§6-6) — 안 움직인 박스에 `changed`를 붙이면
      // 변경 건수가 부풀고 표식이 화면을 덮는다.
      .map(box => (movedKeys.has(groupKeyOf(box.id)) ? syncActivity(box, toRow) : box));

    // JC 재설정(§7-59) — **옮긴 뒤 이웃이 바뀐 쌍만.** 대상 행은 옮긴 unit 과 그 새 양옆,
    // 떠난 행은 빠진 자리에서 **새로 맞닿게 된 둘**이다.
    // ⚠️ 떠난 행의 대상 키는 **박스를 빼기 전에** 구해야 한다 — 빼고 나면 누가 이웃이었는지 모른다.
    const leftBehindKeys = sameRow ? null : jcDirtyKeysOf(fromRow.boxes, unitKey);
    const jcBoxes = reassignRowJc(nextBoxes, toRow, jcDirtyKeysOf(nextBoxes, unitKey));

    setRawRows(prev => prev.map((row, index) => {
      if (index === toIndex) return { ...row, boxes: reflowBoxes(jcBoxes) };
      // 떠난 행도 다시 계산한다 — 빠진 자리 양옆이 이제 서로 떨어져 있다.
      if (index === fromIndex) {
        const rest = row.boxes.filter(box => groupKeyOf(box.id) !== unitKey);
        // 옮겨간 unit 은 빠졌으니 그 키는 빼고, 남은 양옆만 다시 정한다.
        leftBehindKeys?.delete(unitKey);
        return { ...row, boxes: reflowBoxes(reassignRowJc(rest, row, leftBehindKeys)) };
      }
      return row;
    }));

    setSelectedRowKey(toRow.rowKey);
    return true;
  }, [notify, t, reassignRowJc]);

  /**
   * 리사이즈 확정 — 이동과 달리 **행이 바뀌지 않고 duration이 바뀐다.**
   *
   * 미리보기 단계에서 만든 `resizedBoxes`(양 끝이 옮겨진 그룹 박스, 이미 클램프까지 끝난 값)를
   * 그대로 받아 **미리보기와 같은 `resizeSweep`**을 한 번 더 돈다 — 자리는 그대로 두고
   * (`newStart = unit.s`) 늘어난 만큼 이웃을 민다. 길이는 이미 `resizedBoxes`에 반영돼 있다.
   *
   * 미리보기가 이미 한계까지 줄여 놓았으므로 여기서는 클램프가 개입할 여지가 없다
   * (`from === wanted`) — 실패하면 그 길이 자체가 불가한 것이라 거부한다.
   */
  /**
   * **전체 계획 당기기** — 선택 작업 기준으로 한쪽을 여유 없이 압착한다(§7-57).
   *
   * 범위는 **선택 작업이 있는 행 하나**다(old 도 `resourceCd` + 그 행의 활동만 보냈다).
   * 규칙·JC 재파생은 `pullRow` 가 전부 처리한다 — 여기는 스냅샷·속성 되쓰기·알림만 맡는다.
   *
   * ⚠️ 스윕(`applyShrink`/`applySlide`)을 거치지 않는다. 당기기는 **모든 그룹이 의도적으로
   * 움직이는** 조작이라 `unitsToBoxes` 의 `homeS` 바닥(§7-29c)이 걸리면 아무것도 안 당겨진다.
   */
  const commitPull = useCallback((direction, rowKey, unitKey) => {
    const rows = rawRowsRef.current;
    const index = rows.findIndex(row => row.rowKey === rowKey);
    if (index === -1 || !unitKey) return;

    const row = rows[index];
    const result = pullRow({
      boxes: row.boxes,
      downtimes: row.downtimes,
      anchorKey: unitKey,
      direction,
      ownerKeyOf: groupKeyOf
    });

    if (result.movedKeys.size === 0) {
      // `N` 이라 막힌 것과 애초에 당길 게 없는 것을 구분해 준다 — 둘 다 "아무 일도 안 일어남"이라
      // 같은 메시지를 쓰면 사용자가 버튼이 고장난 줄 안다.
      notify(result.stuckKeys.size > 0
        ? t("FP_MSG_WARN_PULL_BLOCKED")
        : t("FP_MSG_WARN_PULL_ANYMORE"));
      return;
    }

    undoRef.current.push({ rows: [{ rowKey: row.rowKey, boxes: row.boxes }] });

    const nextBoxes = result.boxes
      .map(box => (result.movedKeys.has(groupKeyOf(box.id)) ? syncActivity(box, row) : box));
    setRawRows(prev => prev.map((candidate, i) => (
      i === index ? { ...candidate, boxes: reflowBoxes(nextBoxes) } : candidate
    )));

    // 못 당긴 작업이 섞여 있으면 조용히 넘기지 않는다 — 결과가 "덜 당겨진" 것이기 때문이다.
    if (result.stuckKeys.size > 0) notify(t("FP_MSG_WARN_PULL_BLOCKED"));
  }, [notify, t]);

  const commitResize = useCallback(({ rowKey, unitKey, resizedBoxes, mode, applied }) => {
    const rows = rawRowsRef.current;
    const index = rows.findIndex(row => row.rowKey === rowKey);
    if (index === -1) return false;

    const row = rows[index];
    const restBoxes = row.boxes.filter(box => groupKeyOf(box.id) !== unitKey);
    const orderIndex = new Map(buildUnits(row.boxes).map((unit, i) => [unit.key, i]));
    // 리사이즈는 **사용자가 구간 자체를 바꾸는** 조작이라 `netMs` 를 다시 붙인다 — 이동과 달리
    // 순수 소요가 보존 대상이 아니다(늘린 만큼 실작업이 늘어난 것으로 본다).
    const resized = attachNetDuration(resizedBoxes, row.downtimes);
    const moving = buildUnits([...restBoxes, ...resized]).find(unit => unit.key === unitKey);
    if (!moving) return false;

    const result = applied ?? sweepClamped({
      sweep: () => resizeSweep({
        restBoxes, resized, unitKey, mode, orderIndex
      }),
      downtimes: row.downtimes,
      unitKey,
      bound: relaxBound(boundRef.current, row.boxes),
      from: moving.s,
      wanted: moving.s
    });

    if (result.failed) {
      notify(result.blockedBox
        ? t("FP_MSG_CANNOT_MOVE_TO_DOWNTIME", { wo: result.blockedBox?.activity?.woCd ?? "" })
        : t("FP_MSG_OUT_PLAN_BOUND"));
      return false;
    }

    // **한 칸도 못 늘렸으면 알린다** — 이동·슬라이드(`FP_MSG_WARN_MOVE_ANYMORE`)와 같은 계열인데
    // 리사이즈만 빠져 있었다(2026-09-15). 신고 배치처럼 끝점 앞에 48h 짜리 휴무가 있으면 커서가
    // 밴드를 통과하는 내내 길이가 **정확히 그대로**다(`Y` 는 종료가 휴무 안에 놓일 수 없다).
    // 그동안 아무 피드백이 없어 "먹통"으로 읽힌다.
    //
    // `result.clamped` 로는 못 잡는다 — 밴드 안 끝점도 규칙이 **성공**시키기 때문이다(길이만 포화).
    // 그래서 **실제 좌표가 그대로인지**로 판정한다.
    const mainBefore = row.boxes.find(box => box.id === unitKey);
    const mainAfter = result.boxes.find(box => box.id === unitKey);
    if (mainBefore && mainAfter && mainBefore.s === mainAfter.s && mainBefore.e === mainAfter.e) {
      notify(t("FP_MSG_WARN_RESIZE_ANYMORE"));
      return false;
    }

    undoRef.current.push({ rows: [{ rowKey: row.rowKey, boxes: row.boxes }] });

    const movedKeys = movedKeysOf(result.boxes, unitKey);
    const nextBoxes = result.boxes
      .map(box => (movedKeys.has(groupKeyOf(box.id)) ? syncActivity(box, row) : box));

    setRawRows(prev => prev.map((candidate, i) => (i === index ? { ...candidate, boxes: reflowBoxes(nextBoxes) } : candidate)));
    return true;
  }, [notify, t]);

  /**
   * 슬라이드 확정 — old `GanttRunMoveRight:'Slide'`(우클릭 드래그).
   * 같은 행 안에서 순서를 유지한 채 양쪽 빈 공간을 줄이며 움직이고, 닿으면 이웃을 민다.
   * 스윕만 `applySlide`로 갈릴 뿐 나머지(스냅샷·속성 되쓰기·변경 건수)는 이동과 같다.
   */
  const commitSlide = useCallback(({ rowKey, unitKey, newStart, applied }) => {
    const rows = rawRowsRef.current;
    const index = rows.findIndex(row => row.rowKey === rowKey);
    if (index === -1) return false;

    const row = rows[index];
    const units = buildUnits(row.boxes);
    // 미리보기를 거치지 않는 진입점(버튼 등)도 같은 제약을 받아야 한다.
    // 슬라이드도 이동이다 — 같은 분할타입 규칙을 적용한다(`netMs` 가 보존되므로 길이가 새 위치에 맞게 잡힌다).
    const fromTs = units.find(unit => unit.key === unitKey)?.s ?? newStart;
    const result = applied ?? sweepClamped({
      sweep: start => applySlide({ units, movingKey: unitKey, newStart: start }),
      downtimes: row.downtimes,
      unitKey,
      bound: relaxBound(boundRef.current, units),
      from: fromTs,
      wanted: workingStart(fromTs, newStart, row.downtimes)
    });

    if (result.failed) {
      notify(result.blockedBox
        ? t("FP_MSG_CANNOT_MOVE_TO_DOWNTIME", { wo: result.blockedBox?.activity?.woCd ?? "" })
        : t("FP_MSG_OUT_PLAN_BOUND"));
      return false;
    }

    // 갈 수 있는 데까지 물렸는데 **한 칸도 못 움직였다** — 왜 안 움직이는지 알려준다(2026-09-11 요청).
    // ⚠️ `clamped` 만 보면 "20h 끌었는데 18h 갔다"에도 뜨는데, 그건 화면으로 이미 보이는 사실이라
    // 소음이다. **전혀 안 움직인 경우만** 설명이 필요하다.
    if (result.clamped && result.start === fromTs) {
      notify(t("FP_MSG_WARN_MOVE_ANYMORE"));
      return false;
    }

    undoRef.current.push({ rows: [{ rowKey: row.rowKey, boxes: row.boxes }] });

    const movedKeys = movedKeysOf(result.boxes, unitKey);
    const nextBoxes = result.boxes
      .map(box => (movedKeys.has(groupKeyOf(box.id)) ? syncActivity(box, row) : box));

    setRawRows(prev => prev.map((candidate, i) => (i === index ? { ...candidate, boxes: reflowBoxes(nextBoxes) } : candidate)));
    return true;
  }, [notify, t]);

  const undo = useCallback(() => {
    const snapshot = undoRef.current.pop();
    if (!snapshot) return;

    setRawRows(prev => prev.map(row => {
      const restored = snapshot.rows.find(entry => entry.rowKey === row.rowKey);
      return restored ? { ...row, boxes: reflowBoxes(restored.boxes) } : row;
    }));
  }, []);

  // ── 드래그 ────────────────────────────────────────────────────────────────
  // 잡을 수 있는 박스인지 판정하고 드래그 상태를 연다. **false를 돌려주면 프레임은 아무 일도 안 한다**
  // (그 경우 클릭·패닝 등 기존 경로가 그대로 산다).
  //
  // 엣지 드래그 리사이즈는 §7-15a로 구현됐다(`resizeEdgeAt` → `dragRef.resizeEdge` → `commitResize`).
  // ⚠️ **남은 과제**: 저장 경로가 살아나면 duration 변경에 따른 `qty`·공정능력 재계산 규칙을 백엔드와
  //    정해야 한다. 지금은 저장이 없어 화면 내 what-if로만 남으므로 실데이터를 오염시키지 않는다.
  // TODO(MIG-T2): [FP_ADJUST_GANTT] PDF 내보내기 미이관.
  //   old: `exportToPDF` → TreeGrid `ActionPrint()`
  //   사유: 자원 간트와 같은 이유(§5-2a D9) — 캔버스 PNG 한 장으로 좌측 pane이 담기지 않는다
  const handleDragStart = useCallback((row, { ts, button }) => {
    // **바깥 손잡이를 먼저 본다** — 커서가 박스 밖이라 `findBoxAt`은 못 찾는다(§7-15g).
    // 우클릭(Slide)에는 리사이즈를 붙이지 않으므로 그때는 건너뛴다.
    const outer = button === 2 ? null : outerResizeEdgeAt(row, ts);
    const hit = outer ? outer.box : findBoxAt(row, ts);
    if (!hit) return false;

    // JC 띠를 잡아도 딸린 작업(그룹)이 움직인다 — old `GanttRunJoined:'4'`(강체 처리).
    const unitKey = groupKeyOf(hit.id);
    const groupBoxes = row.boxes.filter(box => groupKeyOf(box.id) === unitKey);
    const main = groupBoxes.find(box => box.type !== "jc") ?? hit;

    if (main.inBound === false) {
      notify(t("FP_MSG_WARN_MOVE_TIME"));
      return false;
    }

    // **압축형 기준으로 잰다**(§7-51) — 떨어진 JC 가 있으면 `min~max` 가 휴무를 품어 덩어리가
    // 실제보다 몇 배 커지고, 그 값이 `grabOffset`·`duration`·클램프 기준에 전부 흘러든다.
    // `buildUnits` 가 같은 압축을 하므로 **두 좌표계가 어긋나지 않게** 여기서도 같이 접는다.
    const packed = compactGroup(groupBoxes);
    const start = Math.min(...packed.map(box => box.s));
    const end = Math.max(...packed.map(box => box.e));

    closeTooltip();
    setHoverId(null);
    selectBox(main, row);

    dragRef.current = {
      fromRowKey: row.rowKey,
      unitKey,
      // **우클릭 드래그 = Slide**(old `GanttRunMoveRight:'Slide'`) — 같은 행 안에서만 수평 이동한다.
      // 좌클릭은 행간 이동(자원 재배정)이라 두 제스처의 뜻이 갈린다(§6-2).
      slide: button === 2,
      // 커서가 손잡이 위면 리사이즈, 아니면 이동. 판정은 커서 모양과 **같은 함수**를 쓴다.
      // JC 띠를 잡았을 땐 `hit.type === "jc"`라 `resizeEdgeAt`이 null을 주므로 자연히 이동이 된다.
      // 우클릭 슬라이드에는 리사이즈를 붙이지 않는다 — 한 제스처에 두 뜻을 주면 예측이 안 된다.
      // 바깥 손잡이(폭 무관) 우선, 없으면 안쪽 손잡이(넓은 바만).
      resizeEdge: button === 2 ? null : (outer?.edge ?? resizeEdgeAt(hit, ts)),
      // 리사이즈는 **움직이지 않는 쪽 끝**이 기준이다 — 드래그 시작 시점의 본 활동 양 끝을 고정으로 들고 간다.
      mainStart: main.s,
      mainEnd: main.e,
      // 출발 자리 — 실이동에서는 바가 미리보기 위치로 옮겨가므로 "어디서 시작했나"를 여기 남긴다.
      originRowKey: row.rowKey,
      originStart: start,
      originEnd: end,
      duration: end - start,
      grabOffset: ts - start,
      allowed: new Set((altResourcesRef.current?.[main.activity?.routeCd] ?? []).map(alt => alt.resourceCd)),
      preview: null
    };
    return true;
  }, [findBoxAt, resizeEdgeAt, outerResizeEdgeAt, closeTooltip, selectBox, notify, t]);

  const handleDragMove = useCallback((row, { ts: pointerTs, axisTs, inLeftPane }) => {
    const drag = dragRef.current;
    if (!drag) return;

    const redraw = () => frameApiRef.current?.redraw();

    // 미리보기를 걷어낸다 = 커밋 상태 그대로 그린다(금지 판정일 때). 실이동은 "놓을 수 있을 때"만
    // 보여줘야 한다 — 놓지 못하는 자리로 바가 미리 움직이면 화면이 거짓말을 한다.
    const clearPreview = (blocked) => {
      drag.preview = blocked ?? null;
      drag.previewRows = null;
      drag.pushedKeys = null;
      redraw();
    };

    // 이동/슬라이드/리사이즈 셋 다 스윕 뒤에 `applyDivideRules`를 거치고 **그 결과 배열**을 직접
    // 넣는다(예전의 `setPreviewRows(units)` 헬퍼는 규칙이 붙으면서 없어졌다) — 규칙이 길이를
    // 바꿀 수 있으므로 미리보기는 units 가 아니라 **규칙까지 적용된 박스**를 그려야 커밋과 같다.

    // **좌측 pane(공장·자원 컬럼) 진입의 뜻이 제스처마다 다르다** — 2026-09-10 신고:
    // "시작 지점까지 끌어놓고 커서를 조금만 자원 컬럼 쪽으로 옮기면 바로 원위치로 튕긴다".
    // 원인은 프레임이 pane 위에서 `ts = null` 을 주는데(`GanttFrame:391`) 그걸 무조건 취소로 읽은 것.
    // 왼쪽 끝까지 끌면 커서가 pane 경계에 딱 붙으므로 1px 만 넘어가도 걸렸다(오른쪽 끝은 pane 에서
    // 멀어 증상이 없었다 — 알고리즘 차이가 아니라 기하학적 이유다).
    //
    //   리사이즈·슬라이드 : **행과 무관한 조작**이다(리사이즈는 아래에서 `row` 를 아예 무시한다).
    //                      시간축 좌표만 있으면 되므로 `axisTs` 로 계속 간다 — 계획 구간 밖으로
    //                      나가면 `sweepClamped` 가 벽에 붙인다(§7-29 규칙 5).
    //   이동(좌클릭)      : 대상 **자원 행을 고르는** 조작이라 pane 위에는 놓을 자리가 없다 → 취소.
    const ts = drag.resizeEdge || drag.slide ? (pointerTs ?? axisTs) : pointerTs;

    if (ts == null) {
      clearPreview();
      return;
    }

    // ── 리사이즈 ────────────────────────────────────────────────────────────
    // **행을 따라가지 않는다** — 세로로 움직여도 대상은 늘 잡은 행이다(끝점만 옮기는 조작이라
    // 자원 재배정과 섞이면 안 된다). 그래서 프레임이 넘겨준 `row`는 무시한다.
    if (drag.resizeEdge) {
      const fromRow = rawRowsRef.current.find(candidate => candidate.rowKey === drag.fromRowKey);
      if (!fromRow) return;

      const units = buildUnits(fromRow.boxes);
      const edge = resolveResizeEdge({
        units,
        movingKey: drag.unitKey,
        mode: drag.resizeEdge,
        ts,
        mainStart: drag.mainStart,
        mainEnd: drag.mainEnd,
        bound: relaxBound(boundRef.current, units),
        snap: SNAP_MS
      });

      if (edge.status === DROP_BLOCKED || edge.edgeTs == null) {
        clearPreview({ rowKey: fromRow.rowKey, status: DROP_BLOCKED, start: null, reason: "bound" });
        return;
      }

      // 새 끝점을 실제 박스에 반영한 뒤(JC 동반) 이동과 **같은 스윕**을 돌린다.
      const inGroup = (box) => groupKeyOf(box.id) === drag.unitKey;
      const groupBoxes = fromRow.boxes.filter(inGroup);
      const restBoxes = fromRow.boxes.filter(box => !inGroup(box));
      // 리사이즈 전 순서 — `Map` 이어야 한다(`resizeSweep` 주석). 드래그 중 안 변하므로 프레임당 1회면 된다.
      const orderIndex = new Map(buildUnits(fromRow.boxes).map((unit, i) => [unit.key, i]));

      // 리사이즈도 **갈 수 있는 데까지 늘린다** — 뒤가 막혔다고 통째로 되돌리지 않는다
      // (2026-09-08 사용자 요청). 이동과 달리 클램프 대상이 시작시각이 아니라 **끌고 있는 끝점**이라,
      // `sweep` 콜백이 그 끝점으로 박스를 다시 만들고 스윕까지 돈다.
      const sweep = sweepClamped({
        sweep: edgeTs => resizeSweep({
          restBoxes,
          // 커밋(`commitResize`)과 **같은 순서**로 `netMs` 를 다시 붙인다 — 미리보기와 저장이 갈리면 안 된다.
          resized: attachNetDuration(
            resizeGroupBoxes(groupBoxes, drag.resizeEdge, edgeTs), fromRow.downtimes
          ),
          unitKey: drag.unitKey,
          mode: drag.resizeEdge,
          orderIndex
        }),
        // 벽을 한 번만 찾는다 — 행·끄는 끝이 드래그 내내 고정이다(`runClamped` 주석).
        memo: clampMemoOf(drag, `resize:${fromRow.rowKey}:${drag.resizeEdge}`),
        downtimes: fromRow.downtimes,
        unitKey: drag.unitKey,
        bound: relaxBound(boundRef.current, units),
        // 드래그 시작 시점의 끝점이 기준 — 그 길이는 항상 가능하다.
        from: drag.resizeEdge === RESIZE_LEFT ? drag.mainStart : drag.mainEnd,
        // 하한은 **순수 소요**로 막는다 — `resolveResizeEdge` 의 벽시계 검사만으로는 휴무를 품은
        // 작업이 순수 0 이 된 뒤에도 계속 끌린다.
        //
        // ⚠️ **`workingStart`(휴무 앞 클램프)는 좌측 엣지에만 건다**(§7-38).
        // 그 클램프의 전제는 "드래그 목표는 휴무 안에 있을 수 없다"인데, **끝점은 그렇지 않다** —
        // `I` 는 종료가 휴무 안에 놓일 수 있다(엔진 `forwardDateTimePeriodI` 가 `휴무시작 + remain`
        // 을 돌려준다). `Y` 도 손해가 없다: `applyDivideRules` 가 순수 소요로 끝을 다시 잡아 어차피
        // 휴무 시작에서 멈추므로, 클램프는 **한 격자 칸 일찍 멈추게 할 뿐**이었다.
        // `N` 은 `placeBox` 가 겹치면 거부한다. 즉 타입 분기 없이 끝 엣지에서 빼는 게 맞고,
        // 최종 위치는 규칙이 정확히 잡는다. **시작 엣지는 전제가 그대로라 유지한다.**
        wanted: clampResizeEdge(
          drag.resizeEdge === RESIZE_LEFT
            ? workingStart(drag.mainStart, edge.edgeTs, fromRow.downtimes)
            : edge.edgeTs,
          {
            left: drag.resizeEdge === RESIZE_LEFT,
            mainStart: drag.mainStart,
            mainEnd: drag.mainEnd,
            minMs: MIN_DURATION_MS,
            downtimes: fromRow.downtimes
          }
        )
      });

      if (sweep.failed) {
        clearPreview(sweep.blockedBox
          ? {
            rowKey: fromRow.rowKey, status: DROP_BLOCKED, start: null,
            reason: "downtime", blockedWoCd: sweep.blockedBox?.activity?.woCd
          }
          : { rowKey: fromRow.rowKey, status: DROP_BLOCKED, start: null, reason: "bound" });
        return;
      }

      // 밀림 예고도 **실제 최종 좌표**로 판정한다(`movedKeysOf` 주석) — 스윕 보고를 쓰면
      // 규칙이 되돌려 놓을 박스까지 "밀린다"고 예고한다.
      const pushed = movedKeysOf(sweep.boxes);
      pushed.delete(drag.unitKey);

      drag.preview = {
        rowKey: fromRow.rowKey,
        status: pushed.size > 0 ? DROP_PUSH : DROP_FREE,
        start: ruledStartOf(sweep.boxes, drag.unitKey, sweep.mainStart),
        end: ruledEndOf(sweep.boxes, drag.unitKey, sweep.mainEnd),
        resizedBoxes: sweep.resizedBoxes,
        applied: sweep
      };
      drag.previewRows = new Map([[fromRow.rowKey, sweep.boxes]]);
      drag.pushedKeys = pushed;
      redraw();
      return;
    }

    // ── 슬라이드(우클릭) ────────────────────────────────────────────────────
    // 리사이즈와 같이 **행을 따라가지 않는다.** 순서도 바뀌지 않으므로 "몸통 위 금지"·경계 흡착 같은
    // 삽입 판정이 필요 없다 — 어디로 끌든 이웃을 밀며 그 자리로 간다(계획 구간만 지키면 된다).
    if (drag.slide) {
      const fromRow = rawRowsRef.current.find(candidate => candidate.rowKey === drag.fromRowKey);
      if (!fromRow) return;

      const units = buildUnits(fromRow.boxes);

      // 커서 변위(앵커와 무관). `grabOffset` 이 `originStart` 기준이라 둘이 상쇄돼 `ts - 잡은시각`이다.
      const delta = snapTs(ts - drag.grabOffset - drag.originStart, SNAP_MS);
      // 스윕·클램프는 **unit 좌표**(그룹 시작)로 돈다. 아래 휴무 클램프만 활동 좌표로 하므로
      // 그 결과를 이 오프셋으로 되돌린다. 드래그 중 안 변한다(둘 다 시작 시점 값).
      const mainOffset = drag.mainStart - drag.originStart;

      // 제약에 걸리면 되돌리지 않고 **갈 수 있는 데까지** 민다 — `sweep.start`가 그 한계점이다.
      const sweep = sweepClamped({
        // ⚠️ **스윕 안에서는 경계를 보지 않는다**(`bound: null`). 스윕은 규칙 적용 **전** 길이로
        // 이웃을 밀기 때문에 중간 형상이 최종보다 훨씬 극단적이다 — 휴무를 품던 앞 작업을 통째로
        // 밀어내는 식이라, 규칙이 정리하면 멀쩡할 배치까지 여기서 거부됐다(2026-09-09 신고:
        // "밴드 끝까지만 가고 더는 안 밀린다"). 경계는 `sweepClamped` 가 **규칙 적용 후**에 본다.
        sweep: start => applySlide({ units, movingKey: drag.unitKey, newStart: start }),
        memo: clampMemoOf(drag, `slide:${fromRow.rowKey}`),
        downtimes: fromRow.downtimes,
        unitKey: drag.unitKey,
        bound: relaxBound(boundRef.current, units),
        from: drag.originStart,
        // 변위 스냅 — 원래 시작 기준 1시간 격자라 분·초·ms가 보존된다(§7-15e).
        // 그 결과가 휴무 안이면 **밴드 직전 칸에 세운다**(`shiftDragStart` 주석).
        //
        // ⚠️ **기준은 그룹 시작이 아니라 `mainStart`(활동)다**(§7-45, 2026-09-15 신고).
        // 그룹 시작은 **떨어진 prev JC** 일 수 있는데(§7-40 원인 ①~⑤), 그 JC 가 휴무 바로 앞에
        // 서 있으면 오른쪽으로 아무리 끌어도 목표가 계속 밴드 안이라 클램프가 **같은 자리로
        // 되돌린다** — 밴드 폭만큼(실측 57h) 통째로 먹통이 된다. 정작 활동은 휴무 뒤에 있어
        // 제약이 없는데도 그렇다. 클램프의 전제("시작시각은 휴무 안에 있을 수 없다")가 적용되는
        // 대상은 **활동**이지 파생값인 JC 가 아니다 — 묶음 D 와 같은 원칙이다.
        wanted: shiftDragStart(drag.mainStart, delta, fromRow.downtimes, SNAP_MS) - mainOffset
      });

      if (sweep.failed) {
        clearPreview(sweep.blockedBox
          ? {
            rowKey: fromRow.rowKey, status: DROP_BLOCKED, start: null,
            reason: "downtime", blockedWoCd: sweep.blockedBox?.activity?.woCd
          }
          : { rowKey: fromRow.rowKey, status: DROP_BLOCKED, start: null, reason: "bound" });
        return;
      }

      // 밀림 예고도 **실제 최종 좌표**로 판정한다(`movedKeysOf` 주석) — 스윕 보고를 쓰면
      // 규칙이 되돌려 놓을 박스까지 "밀린다"고 예고한다.
      const pushed = movedKeysOf(sweep.boxes);
      pushed.delete(drag.unitKey);

      drag.preview = {
        rowKey: fromRow.rowKey,
        status: pushed.size > 0 ? DROP_PUSH : DROP_FREE,
        start: ruledStartOf(sweep.boxes, drag.unitKey, sweep.start),
        end: ruledEndOf(sweep.boxes, drag.unitKey, sweep.start + drag.duration),
        applied: sweep
      };
      drag.previewRows = new Map([[fromRow.rowKey, sweep.boxes]]);
      drag.pushedKeys = pushed;
      redraw();
      return;
    }

    // 이동 제스처는 위 가드에서 pane 진입(`pointerTs == null`)이 이미 걸러진다. 여기 남은 건
    // 행 밖(위/아래 여백)으로 나간 경우다 — `inLeftPane` 은 방어적으로 같이 둔다.
    if (!row || inLeftPane) {
      clearPreview();
      return;
    }

    const sameRow = row.rowKey === drag.fromRowKey;
    // 다른 자원으로 옮길 때는 **대체자원만 허용**한다(old OnGanttRunDrop의 FP_WARN_MOVE_RESOURCE 가드).
    if (!sameRow && !drag.allowed.has(row.resourceCd)) {
      clearPreview({ rowKey: row.rowKey, status: DROP_BLOCKED, start: null, reason: "resource" });
      return;
    }

    const rows = rawRowsRef.current;
    const fromRow = rows.find(candidate => candidate.rowKey === drag.fromRowKey);
    const movingBoxes = (fromRow?.boxes ?? []).filter(box => groupKeyOf(box.id) === drag.unitKey);
    const targetBoxes = sameRow ? (row.boxes ?? []) : [...(row.boxes ?? []), ...movingBoxes];
    const units = buildUnits(targetBoxes);

    // **커서가 곧 활동 시작이다**(§7-53, 2026-09-16 사용자 요청).
    // 옛 동작은 `grabOffset = ts - 그룹시작`(잡은 지점의 상대 위치 보존)이었는데, 휴무를 품어
    // 늘어난 `Y` 작업은 화면상 수십 시간이라 **끝 근처를 잡으면 미리보기가 커서에서 그만큼
    // 멀어져** 놓을 자리를 겨눌 수가 없었다.
    //
    // 이 제스처는 바를 **옮겨 그리지 않고 자리표시만** 한다(§7-15h) — 그래서 커서에 맞춰도
    // "잡은 순간 바가 커서로 순간이동"하는 부작용이 없다. **슬라이드·리사이즈는 실이동이라
    // 그대로 둔다**(거기서 같은 짓을 하면 바가 손에서 튄다).
    //
    // `leadMs`(= prev JC 길이)만큼 빼서 넘긴다 — `resolveDrop` 은 **그룹 시작**을 다루므로,
    // 커서를 **활동** 시작에 맞추려면 그 차이를 여기서 보정해야 한다. JC 가 없으면 0 이다.
    const leadMs = drag.mainStart - drag.originStart;

    const drop = resolveDrop({
      units,
      movingKey: drag.unitKey,
      duration: drag.duration,
      ts,
      grabOffset: leadMs,
      bound: relaxBound(boundRef.current, units),
      snap: SNAP_MS,
      // 변위 스냅의 기준점 — 이 값의 분·초·ms가 그대로 유지된다(§7-15e).
      originStart: drag.originStart,
      // 화면 픽셀 허용치를 시각 단위로 환산해서 넘긴다 — 순수 함수는 줌/스케일을 몰라야 한다.
      seamTolerance: scaleRef.current ? DROP_SEAM_PX / scaleRef.current.pxPerMs : 0
    });

    if (drop.status === DROP_BLOCKED || drop.start == null) {
      clearPreview({ rowKey: row.rowKey, status: DROP_BLOCKED, start: null, reason: "bound" });
      return;
    }

    // **좌드래그에도 휴무 클램프를 건다**(§7-52, 2026-09-16 신고).
    // 슬라이드(`shiftDragStart`)·리사이즈(`workingStart`)는 목표가 휴무 안이면 밴드 직전 격자로
    // 되돌리는데 **이동만 빠져 있었다** — `resolveDrop` 은 휴무를 아예 보지 않는다.
    // 그래서 `pushStartOutOfDowntime` 주석의 전제("여기까지 밴드 안 시작이 내려오는 건 스윕에
    // 밀린 이웃뿐이다")가 이 제스처에서 깨졌고, **원본 시작의 분·초에 따라 결과가 갈렸다**:
    // 변위 스냅이 분을 보존하므로 `:00` 작업만 밴드 시작 **정각**에 설 수 있고, 그 자리는
    // `start > dt.s` 가 거짓이라 밀리지 않아 **휴무 앞 작업량이 0 인 배치**가 나온다(엔진이
    // 만들지 않는 형태라 저장 후 재계획에서 조용히 되돌아간다). `:24` 작업은 밴드 **안**에
    // 떨어져 밀려나고 JC 가 밴드 앞으로 분리된다 — 같은 조작인데 결과가 둘로 갈린 원인이다.
    //
    // ⚠️ **기준은 활동 시작이다**(§7-45 와 같은 함정) — 그룹 시작으로 재면 떨어진 JC 때문에
    // 그룹은 밴드 밖인데 활동은 안쪽인 경우를 놓친다.
    // ⚠️ **밴드 안일 때만 손댄다** — 경계 흡착(`seam`)으로 들어온 값은 격자 위가 아니라서
    // 무조건 다시 스냅하면 "붙이기는 정확해야 한다"는 규칙이 깨진다.
    const dropDowntimes = normalizeDowntimes(row.downtimes);
    const wantedMain = drop.start + leadMs;
    const clampedMain = clampStartBeforeDowntime(wantedMain, dropDowntimes, drag.mainStart, SNAP_MS);
    const dropStart = clampedMain === wantedMain ? drop.start : clampedMain - leadMs;

    // **판정이 났으면 항상 스윕까지 돌린다** — 예전에는 밀림(PUSH)일 때만 돌려서 고스트를 그렸지만,
    // 실이동은 밀림이 없는 경우에도 "잡은 바가 그 자리로 옮겨간 모습"을 그려야 한다.
    //
    // 분할타입 규칙은 스윕과 함께 판정한다 — **재배치된 행 전체**가 대상이다(이동 대상 + 밀려난 이웃).
    // N 타입이 휴무에 걸치면 그 위치가 불가해지고, Y 타입은 새 위치의 휴무 구성에 맞춰 길이가 다시 잡힌다.
    // 걸리면 되돌리지 않고 **갈 수 있는 데까지** 간다 — `sweep.start`가 그 한계점이다.
    const sweep = sweepClamped({
      sweep: start => applyShrink({ units, movingKey: drag.unitKey, newStart: start }),
      memo: clampMemoOf(drag, `move:${row.rowKey}`),
      downtimes: row.downtimes,
      unitKey: drag.unitKey,
      bound: relaxBound(boundRef.current, units),
      from: units.find(unit => unit.key === drag.unitKey)?.s ?? dropStart,
      // 이미 위에서 **활동 기준**으로 클램프했다 — 여기서 그룹 기준으로 또 걸면 §7-45 의 그 버그다.
      wanted: dropStart
    });

    if (sweep.failed) {
      clearPreview(sweep.blockedBox
        ? {
          rowKey: row.rowKey, status: DROP_BLOCKED, start: null,
          reason: "downtime", blockedWoCd: sweep.blockedBox?.activity?.woCd
        }
        : { rowKey: row.rowKey, status: DROP_BLOCKED, start: null, reason: "bound" });
      return;
    }

    const pushed = movedKeysOf(sweep.boxes);
    pushed.delete(drag.unitKey);

    drag.preview = {
      rowKey: row.rowKey,
      status: pushed.size > 0 ? DROP_PUSH : DROP_FREE,
      // 제약·클램프 후 값을 쓴다 — 커밋(`commitMove`)이 이 값을 그대로 받으므로 미리보기와 저장이 일치한다.
      start: ruledStartOf(sweep.boxes, drag.unitKey, sweep.start),
      // ⚠️ **고스트·툴팁의 끝시각은 규칙 적용 후 값**이어야 한다. `drop.start + drag.duration`
      // (= 옮기기 전 늘어난 길이)로 그리면 초록 자리표시는 긴데 실제로는 짧게 놓여
      // "저기로 안 갔다"로 보인다(2026-09-08 사용자 지적).
      end: ruledEndOf(sweep.boxes, drag.unitKey, sweep.start + drag.duration),
      applied: sweep
    };
    // 행간 이동이면 **출발 행에서도 그 작업이 빠진 모습**을 보여준다 — 한쪽만 반영하면 같은 작업이
    // 두 행에 동시에 있는 화면이 된다.
    drag.previewRows = new Map([[row.rowKey, sweep.boxes]]);
    if (!sameRow) {
      drag.previewRows.set(drag.fromRowKey, (fromRow?.boxes ?? []).filter(box => groupKeyOf(box.id) !== drag.unitKey));
    }
    drag.pushedKeys = pushed;
    redraw();
  }, []);

  const handleDragEnd = useCallback((row, { moved }) => {
    const drag = dragRef.current;
    dragRef.current = null;
    frameApiRef.current?.redraw();
    if (!drag) return;

    // 제자리에서 눌렀다 뗀 것은 선택으로만 본다(클릭 경로는 프레임이 억제하므로 여기서 처리).
    if (!moved) return;

    const preview = drag.preview;
    if (!preview || preview.status === DROP_BLOCKED || preview.start == null) {
      if (preview?.reason === "downtime") {
        // 분할 불가(`divide_tp_cd='N'`) 작업이 휴무에 걸쳤다 — 이동 대상일 수도, 밀려난 이웃일 수도 있다.
        notify(t("FP_MSG_CANNOT_MOVE_TO_DOWNTIME", { wo: preview.blockedWoCd ?? "" }));
      } else {
        notify(t(preview?.reason === "resource" ? "FP_WARN_MOVE_RESOURCE" : "FP_MSG_WARN_MOVE_TIME"));
      }
      return;
    }

    // 슬라이드도 리사이즈와 같이 놓은 지점의 행과 무관하다(대상이 늘 잡은 행이다).
    if (drag.slide) {
      if (commitSlide({
        rowKey: preview.rowKey, unitKey: drag.unitKey,
        newStart: preview.start, applied: preview.applied
      })) {
        notify(t("FP_MSG_APPLIED"), snackBarMessageColor.saveSuccess);
      }
      return;
    }

    if (drag.resizeEdge) {
      if (commitResize({
        rowKey: preview.rowKey, unitKey: drag.unitKey,
        resizedBoxes: preview.resizedBoxes, mode: drag.resizeEdge, applied: preview.applied
      })) {
        notify(t("FP_MSG_APPLIED"), snackBarMessageColor.saveSuccess);
      }
      return;
    }

    if (!row || row.rowKey !== preview.rowKey) return;

    if (commitMove({
      fromRowKey: drag.fromRowKey, toRowKey: preview.rowKey, unitKey: drag.unitKey,
      newStart: preview.start, applied: preview.applied
    })) {
      notify(t("FP_MSG_APPLIED"), snackBarMessageColor.saveSuccess);
    }
  }, [commitMove, commitResize, commitSlide, notify, t]);

  const handleDragCancel = useCallback(() => {
    dragRef.current = null;
    frameApiRef.current?.redraw();
  }, []);

  const resolveCursor = useCallback((row, { ts, inLeftPane }) => {
    if (inLeftPane || !row || ts == null) return null;
    // 바깥 손잡이는 박스 밖이라 `findBoxAt`에 걸리지 않는다 → 먼저 본다(§7-15g).
    if (outerResizeEdgeAt(row, ts)) return "ew-resize";

    const box = findBoxAt(row, ts);
    if (!box) return null;
    if (box.inBound === false) return "not-allowed";
    return resizeEdgeAt(box, ts) ? "ew-resize" : "grab";
  }, [findBoxAt, resizeEdgeAt, outerResizeEdgeAt]);

  // ── 툴바 동작 ─────────────────────────────────────────────────────────────
  const runButtonMove = useCallback((mode, direction, double = false) => {
    const { box, row } = selected;
    if (!box || !row) {
      notify(t("MSG_NoSelectedData"));
      return;
    }
    if (box.inBound === false) {
      notify(t("FP_MSG_WARN_MOVE_TIME"));
      return;
    }

    const unitKey = groupKeyOf(box.id);
    const units = buildUnits(row.boxes);
    const moving = units.find(unit => unit.key === unitKey);
    if (!moving) return;

    let newStart;
    if (mode === "attach") {
      newStart = attachStart({ units, movingKey: unitKey, direction, bound: relaxBound(boundRef.current, units) });
      if (newStart == null) {
        notify(t("FP_MSG_WARN_MOVE_ANYMORE"));
        return;
      }
    } else {
      // 1시간 고정(×2 버튼은 2시간). **현재 시작에서 상대 이동**이라 분·초·ms가 보존된다 —
      // 드래그도 같은 격자(변위 스냅)를 쓰므로 두 경로의 결과가 일치한다(§7-15e).
      const step = SNAP_MS * (double ? MOVE_STEP_X2 : 1);
      newStart = moving.s + (direction === "left" ? -step : step);
    }

    if (commitMove({ fromRowKey: row.rowKey, toRowKey: row.rowKey, unitKey, newStart })) {
      notify(t("FP_MSG_APPLIED"), snackBarMessageColor.saveSuccess);
    }
  }, [selected, commitMove, notify, t]);

  // 상세탭 "적용" — 자원 + 시작시각을 지정해 옮긴다(old `moveResource`).
  const handleDetailMove = useCallback(({ resourceCd, startTs }) => {
    const { box, row } = selected;
    if (!box || !row) return;

    const target = rawRowsRef.current.find(candidate => candidate.resourceCd === resourceCd);
    if (!target) {
      notify(t("FP_WARN_MOVE_RESOURCE"));
      return;
    }

    if (commitMove({
      fromRowKey: row.rowKey, toRowKey: target.rowKey, unitKey: groupKeyOf(box.id), newStart: startTs
    })) {
      notify(t("FP_MSG_APPLIED"), snackBarMessageColor.saveSuccess);
    }
  }, [selected, commitMove, notify, t]);

  // TODO(MIG-T2): [FP_ADJUST_GANTT] 보정 결과 저장 / 서버 재배치 검증 미이관.
  //   old: `gantt/activities/save`, `gantt/arranged-activities`
  //   사유: old 프론트도 "미지원" 처리였고 old·new 백엔드 양쪽에서 해당 컨트롤러 블록이 주석
  //         처리돼 있다(`AdjustmentController.java:84~126`). 백엔드 재개가 선행이다.
  //   ⚠️ **전체 당기기(`shiftAll`)는 2026-09-17 프런트에서 구현했다**(§7-57) — 이 목록에서 뺐다.
  //       백엔드 서비스는 old 에도 이미 삭제돼 있어 이식할 참조 구현이 없었고, 프런트 계산 비용이
  //       600작업 행에서 2.6ms 라 서버 왕복이 필요 없다.

  // ── 조회 ──────────────────────────────────────────────────────────────────
  const debouncedRetrieveRef = useRef();
  const computeResourceCds = () => {
    const { selected: picked, options } = searchStateRef.current.resourceState;
    return picked.length === options.length ? [] : picked;
  };

  const retrieve = useCallback(() => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      notify(t("FP_MSG_SELECT_VERSION"));
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      notify(t("FP_MSG_SELECT_PLAN_PERIOD"));
      return;
    }

    const reqId = ++latestReqRef.current;
    const params = {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      resourceCds: computeResourceCds(),
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };

    closeTooltip();
    selectBox(null, null);
    setLoading(true);
    // 조회하면 편집 이력을 버린다 — 새 데이터에 옛 스냅샷을 되돌리면 뒤섞인다.
    undoRef.current = [];

    fpAdjustmentGanttService.getActivities(params)
      .then(response => {
        if (reqId !== latestReqRef.current) return;

        const data = response.data ?? [];

        // 축 구간을 먼저 정한다 — `buildBoxes`의 `inBound`(편집 잠금) 판정이 이 값을 쓴다.
        const dataRange = data.reduce((acc, row) => {
          (row.activities ?? []).forEach(activity => {
            const s = toMs(activity.startTs);
            const e = toMs(activity.endTs);
            if (!Number.isNaN(s)) acc.start = acc.start == null ? s : Math.min(acc.start, s);
            if (!Number.isNaN(e)) acc.end = acc.end == null ? e : Math.max(acc.end, e);
          });
          return acc;
        }, { start: null, end: null });

        const nextBound = buildGanttBound({
          planStartTs: version.selectedVersion?.startTs,
          planEndTs: version.selectedVersion?.endTs,
          startDate: period.startDate,
          endDate: period.endDate,
          isEntirePlanPeriod: period.isEntirePlanPeriod,
          dataStartTs: dataRange.start,
          dataEndTs: dataRange.end
        });

        setBound(nextBound);
        setRawRows(data.map((row, index) => {
          // **정규화해서 저장한다** — `divideMarksOf` 가 매 프레임 불리는데 거기서 다시 정렬·병합하면
          // 낭비다. 나머지 `divideRules` 함수들은 자체적으로 정규화하므로 멱등하게 통과한다.
          const rowDowntimes = normalizeDowntimes(toRanges(row.downtimes));
          return {
            rowKey: `${row.plantCd}-${row.resourceCd}-${index}`,
            plantCd: row.plantCd,
            plantNm: row.plantNm,
            resourceCd: row.resourceCd,
            resourceNm: row.resourceNm,
            downtimes: rowDowntimes,
            // ⚠️ 서버의 `divideYDowntimes` 는 **쓰지 않는다** — 편집하면 곧바로 낡고 부분 겹침이
            // 빠진다(`divideRules.divideMarksOf` 주석). 빗금은 그릴 때 계산한다.
            // 휴무를 먼저 만들어 넘긴다 — 박스의 `netMs` 가 이 값으로 정해진다.
            boxes: buildLaneBoxes(row.activities, nextBound, rowDowntimes)
          };
        }));

        const freezeTs = toMs(version.selectedVersion?.freezeTs);
        const planStartTs = toMs(version.selectedVersion?.startTs);
        setFrozenZone(
          !Number.isNaN(freezeTs) && !Number.isNaN(planStartTs) && freezeTs > planStartTs
            ? { startTs: planStartTs, endTs: freezeTs }
            : null
        );

        // 대체자원 — **조회 1회**만 받는다(old `loadData().then()`의 `loadAltResources`와 같은 자리).
        // 응답은 `Map<routeCd, [자원…]>`이고 요청 파라미터(planScope + 화면 자원 목록)와 `routeCd`는
        // **편집으로 바뀌지 않는다** → 편집마다 다시 받을 이유가 없다. 예전에는 `rawRows`를 의존성으로
        // 둔 effect라 **드롭·버튼 이동·되돌리기마다 같은 응답을 다시 받고 있었다**(2026-08-28 사용자 지적).
        // 자원 집합은 조회를 통해서만 바뀌므로 앵커를 여기 두면 정확히 필요한 만큼만 호출된다.
        // 본조회와 **같은 요청 토큰**을 쓴다 — 늦게 온 응답이 최신 조회의 허용 자원을 덮으면
        // 드롭 판정이 조용히 틀어진다(예전 effect에는 이 가드가 없었다).
        const resourceCds = data.map(row => row.resourceCd).filter(Boolean);
        altResourcesRef.current = {};
        jcIndexRef.current = null;
        if (resourceCds.length > 0) {
          // JC 기준정보 번들 — 화면에 뜬 자원만 받는다. 매트릭스는 자원 × 쌍이라 커질 수 있다.
          fpAdjustmentGanttService.getJcMaster({
            viewId: viewName, subId: "", planScope: version.planScope, resourceCds
          })
            .then(jcResponse => {
              if (reqId !== latestReqRef.current) return;
              jcIndexRef.current = buildJcIndex(jcResponse.data ?? null);
            })
            .catch(e => debug(e));

          fpAdjustmentGanttService.getAltResources({
            viewId: viewName, subId: "", planScope: version.planScope, resourceCds
          })
            .then(altResponse => {
              if (reqId !== latestReqRef.current) return;
              altResourcesRef.current = altResponse.data ?? {};
            })
            .catch(e => debug(e));
        }

        if (data.length === 0) notify(t("MSG_NoData"), snackBarMessageColor.dataEmpty);
      })
      .catch(e => {
        if (reqId === latestReqRef.current) setRawRows([]);
        debug(e);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) setLoading(false);
      });
  }, [viewName, closeTooltip, selectBox, notify, t]);

  const retrieveRef = useRef(retrieve);
  retrieveRef.current = retrieve;
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveRef.current(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  // **변경분이 있으면 되묻는다** — 저장 경로가 없는 화면이라 재조회 한 번에 작업을 통째로 잃는다.
  // old에는 없던 동작이고(§6-6 (5)), 유보가 아니라 추가 항목이다.
  // 확인 창은 저장소 공용 `components/Dialog`를 쓴다(브라우저 `confirm`은 이 앱 어디에도 없다).
  //
  // **조회 버튼이 없으므로 자동 조회 경로가 이 가드를 통과해야 한다**(2026-08-28) — 조회조건이
  // 바뀌면 바로 조회가 나가는 구조라(자원 간트와 같은 규칙) 버튼만 막아서는 아무것도 못 지킨다.
  // 취소하면 조건은 이미 바뀐 채 옛 데이터가 남는다 — 조건을 되돌리는 것보다 작업을 지키는 쪽이 낫다.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // 조회 요청은 의존성 없는 effect에서도 불리므로 최신 건수를 ref로 읽는다.
  const changedCountRef = useRef(0);
  changedCountRef.current = changedCount;

  const requestRetrieve = useCallback(() => {
    if (changedCountRef.current > 0) {
      setConfirmOpen(true);
      return;
    }
    debouncedRetrieveRef.current();
  }, []);
  const requestRetrieveRef = useRef(requestRetrieve);
  requestRetrieveRef.current = requestRetrieve;

  // ── 캔버스 렌더 ───────────────────────────────────────────────────────────
  const barGeomY = useCallback((row, box, rowTop) => (
    { top: rowTop + LANE_BAR_PADDING_Y, height: Math.max(6, row.height - LANE_BAR_PADDING_Y * 2) }
  ), []);

  // 드래그 팁의 기간 표기("3시간 30분"). `formatTimeDiff`는 백엔드가 주는 `h/m/s` 문자열 전용이라
  // 여기서는 못 쓴다 — 우리가 방금 계산한 ms를 같은 단위 라벨로 옮긴다.
  const durationLabel = useCallback((ms) => {
    const totalMinutes = Math.max(0, Math.round(ms / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}${t("FP_MINUTES")}`;
    return minutes === 0 ? `${hours}${t("FP_HOUR")}` : `${hours}${t("FP_HOUR")} ${minutes}${t("FP_MINUTES")}`;
  }, [t]);

  const handleDrawBackground = useCallback(({ ctx, x, width, height }) => {
    boxRectsRef.current = new Map();
    selectedRectRef.current = null;
    selectedJcRectsRef.current = [];
    if (!scale) return;

    drawFrozenZone(ctx, { zone: frozenZone, x, top: 0, height });
    drawWeekends(ctx, { ranges: weekendRanges, x, top: 0, height });
    drawTimelineGrid(ctx, {
      ticks: ticks.bottom, subTicks: ticks.sub,
      x0: x(scale.t0), top: 0, height, viewLeft: 0, viewRight: width
    });
  }, [scale, ticks, weekendRanges, frozenZone]);

  const handleDrawRow = useCallback(({ ctx, x, row, rowTop, rowHeight: rh, width }) => {
    drawRowDivider(ctx, { left: 0, width, y: rowTop + rh });
    drawDowntimes(ctx, { downtimes: row.downtimes, x, rowTop, rowHeight: rh });

    // **제스처마다 미리보기 표현이 다르다**(§7-15h):
    //   - Slide·리사이즈 → **실이동**. 밀림·길이 변화 자체가 관심사라 결과를 봐야 한다(§7-15d).
    //   - 좌드래그(이동) → **자리표시만**. 바를 옮기지 않는다 — 밀림 연쇄가 길 때 여러 바가 출렁이지
    //     않고 원래 상태가 계속 보여 비교가 된다(old도 이 방식이었다).
    // ⚠️ 판정 입력은 어느 쪽이든 계속 커밋 상태(`rawRowsRef`)를 쓴다 — 미리보기를 다음 프레임의
    // 입력으로 쓰면 피드백 루프가 생겨 바가 스스로 기어간다.
    const drag = dragRef.current;
    const livePreview = Boolean(drag && (drag.slide || drag.resizeEdge));
    const previewBoxes = livePreview ? drag.previewRows?.get(row.rowKey) : null;
    // 밀림 표식은 **두 방식 모두** 켠다 — 자리표시 방식에서도 "어느 작업이 영향받는지"는 보여야 한다
    // (위치는 안 움직이고 색만 붙는다).
    const pushedKeys = drag?.preview?.rowKey === row.rowKey && drag.previewRows ? drag.pushedKeys : null;

    const labelJobs = [];
    const changedRects = [];
    const pushedRects = [];
    // 떨어진 JC ↔ 주인 작업 연결선(§7-40 A안). 박스를 다 그린 **뒤** 그린다 — 틈에만 놓이므로
    // 바를 가리지 않지만, 휴무 빗금 위로는 올라와야 보인다.
    const jcLinks = jcLinkGapsOf(previewBoxes ?? row.boxes);

    (previewBoxes ?? row.boxes).forEach(box => {
      const x0 = x(box.s);
      const x1 = x(box.e);
      if (x1 < -40 || x0 > width + 40) return;

      const { top, height } = barGeomY(row, box, rowTop);
      const geom = { left: x0, width: Math.max(1, x1 - x0), top, height };
      const isSelectedJc = box.type === "jc" && !!selectedId && groupKeyOf(box.id) === selectedId;
      const dim = dimming && !matchFn(box.activity);

      const rect = drawBox(ctx, { box, geom, dim, hovered: hoverId === box.id, withLabel: false });

      if (box.type === "box" && rect) {
        labelJobs.push({ box, rect, dim });
        if (box.changed) changedRects.push(rect);
        // 내 조작 때문에 밀려난 작업 — 실이동에서는 위치로 구분이 안 되니 색으로 말한다.
        if (pushedKeys?.has(groupKeyOf(box.id))) pushedRects.push(rect);
      }
      if (selectedId && box.id === selectedId) selectedRectRef.current = rect;
      if (isSelectedJc && rect) selectedJcRectsRef.current.push(rect);
      if (box.id) boxRectsRef.current.set(box.id, { x0: geom.left, x1: geom.left + geom.width, cy: top + height / 2 });
    });

    // 분할 마크(빗금) — **그릴 때 분할타입으로 계산한다**(§7-37). 서버의 `divideYDowntimes` 를
    // 쓰던 때는 편집해도 안 바뀌어 **작업을 휴무로 옮겨도 빗금이 안 생겼다**(2026-09-10 신고).
    // `previewBoxes` 를 우선 쓰므로 **드래그 중에도 실시간으로** 따라온다.
    //   Y → 겹치는 만큼 빗금 / N → 겹칠 수 없음 / I → 휴무도 작업이라 빗금 없음
    const { top, height } = barGeomY(row, { track: 0 }, rowTop);
    drawDivideMarks(ctx, {
      marks: divideMarksOf(previewBoxes ?? row.boxes, row.downtimes),
      x, barTop: top, barHeight: height, searchActive: dimming, matchFn
    });

    jcLinks.forEach(gap => {
      const x0 = x(gap.s);
      const x1 = x(gap.e);
      if (x1 < 0 || x0 > width) return;
      const geomY = barGeomY(row, { track: 0 }, rowTop);
      drawJcLink(ctx, { geom: { left: x0, width: x1 - x0, top: geomY.top, height: geomY.height } });
    });

    // 마지막에 라벨 — 마크의 밝은 면 위로 올라온다(§7-20).
    labelJobs.forEach(job => drawBoxLabel(ctx, job));
    // 변경 표식은 라벨 위에 — 라벨보다 뒤에 그리면 글자에 가린다.
    changedRects.forEach(rect => drawChangedMark(ctx, { rect }));
    pushedRects.forEach(rect => drawPushedRail(ctx, { rect }));
  }, [barGeomY, dimming, matchFn, selectedId, hoverId]);

  /**
   * 편집 오버레이 — 선택 링 + 드래그 중 표현. 제스처에 따라 둘로 갈린다(§7-15h):
   *   - 좌드래그(이동)   → **자리표시**(파선 + 삽입 caret). 바는 제자리에 있다.
   *   - Slide·리사이즈   → 판정 테두리 + 출발 자리 윤곽. 바는 `handleDrawRow`가 이미 옮겨 그렸다.
   * **모든 행을 그린 뒤** 실행되므로 이웃 바에 덮이지 않는다(§7-9와 같은 이유).
   */
  const handleDrawOverlay = useCallback(({ ctx, x, scrollTop, width }) => {
    const parts = [selectedRectRef.current, ...selectedJcRectsRef.current].filter(Boolean);
    if (parts.length > 0) {
      const left = Math.min(...parts.map(p => p.left));
      const right = Math.max(...parts.map(p => p.left + p.width));
      const base = selectedRectRef.current ?? parts[0];
      // 손잡이 표식은 두지 않는다(2026-08-28 사용자 지시) — hover만으로 커서가 뜨니 "선택해야
      // 잡힌다"를 알릴 필요가 없어졌다. 선택 표현은 링 하나로 끝낸다.
      drawSelectionRing(ctx, { rect: { left, top: base.top, width: right - left, height: base.height } });
    }

    const drag = dragRef.current;
    const preview = drag?.preview;
    if (!drag || !preview) return;

    const row = layout.rows.find(candidate => candidate.rowKey === preview.rowKey);
    if (!row) return;

    const { top, height } = barGeomY(row, null, row.top - scrollTop);
    const bandGeom = (s, e) => ({ left: x(s), width: Math.max(2, x(e) - x(s)), top, height });

    if (preview.status === DROP_BLOCKED || preview.start == null) {
      // 금지일 때는 **행 전체**를 붉게 스치지 않고 커서 주변 구간만 표시한다 — 행을 통째로 칠하면
      // 그 행의 실제 바들이 안 보여서 "왜 안 되는지"가 오히려 가려진다.
      return;
    }

    const end = preview.end ?? preview.start + drag.duration;
    const livePreview = Boolean(drag.slide || drag.resizeEdge);
    const tone = preview.status === DROP_PUSH ? "push" : "ok";

    // 좌드래그(이동) — 바는 제자리에 있고 **놓일 자리만** 파선으로 표시한다(§7-15h).
    // 출발 자리 윤곽은 그리지 않는다 — 원래 바가 그대로 보이므로 중복이다.
    if (!livePreview) {
      drawDropPlaceholder(ctx, { geom: bandGeom(preview.start, end), tone });
      drawDragTip(ctx, {
        x: x(preview.start),
        y: top - 6,
        viewWidth: width,
        text: `${dateUtils.formatDateTime(new Date(preview.start))} ~ ${dateUtils.formatDateTime(new Date(end))}`
      });
      return;
    }

    // 출발 자리 — 실이동으로 사라지는 유일한 정보다(§7-15d). 움직였을 때만 그린다.
    // **끝점 비교도 넣는다** — 우측 리사이즈는 시작이 그대로라 시작만 보면 "안 움직였다"가 되어
    // 원래 길이를 볼 수 없다(길이가 관심사인 조작인데 비교 대상이 사라지는 셈).
    const moved = drag.originRowKey !== preview.rowKey
      || drag.originStart !== preview.start
      || drag.originEnd !== end;

    if (drag.originRowKey && moved) {
      const originRow = layout.rows.find(candidate => candidate.rowKey === drag.originRowKey);
      if (originRow) {
        const geomY = barGeomY(originRow, null, originRow.top - scrollTop);
        drawOriginOutline(ctx, {
          geom: {
            left: x(drag.originStart),
            width: Math.max(2, x(drag.originEnd) - x(drag.originStart)),
            top: geomY.top,
            height: geomY.height
          }
        });
      }
    }

    // 판정(초록/노랑)은 **테두리로만** 두른다 — 그 자리엔 이미 진짜 바가 있어 면을 칠하면 색을 가린다.
    drawDropBand(ctx, { geom: bandGeom(preview.start, end), tone, outlineOnly: true });

    // 리사이즈는 **길이가 관심사**라 팁에 기간을 덧붙인다(이동은 위치가 관심사라 시각 두 개면 충분).
    const span = `${dateUtils.formatDateTime(new Date(preview.start))} ~ ${dateUtils.formatDateTime(new Date(end))}`;
    drawDragTip(ctx, {
      x: x(preview.start),
      y: top - 6,
      viewWidth: width,
      text: drag.resizeEdge ? `${span} (${durationLabel(end - preview.start)})` : span
    });
  }, [layout, barGeomY, durationLabel]);

  // ── 입력 ──────────────────────────────────────────────────────────────────
  const handleRowActivate = useCallback((row, { ts }) => {
    selectBox(findBoxAt(row, ts), row);
  }, [findBoxAt, selectBox]);

  const handleHover = useCallback((row, { ts, clientX, clientY, inLeftPane, colKey }) => {
    // 드래그 중에는 hover를 만들지 않는다 — 툴팁이 고스트를 가리고 리렌더가 드래그를 방해한다.
    if (dragRef.current) return;

    if (inLeftPane) {
      const name = colKey === "resourceCd" ? (row?.resourceNm ?? "") : "";
      const key = name ? row.rowKey : null;
      if (leftTipKeyRef.current === key) return;

      leftTipKeyRef.current = key;
      setHoverId(null);
      closeTooltip();
      if (name) setTooltip({ x: clientX, y: clientY, text: name });
      return;
    }
    leftTipKeyRef.current = null;

    const box = row ? findBoxAt(row, ts) : null;
    if (!box || box.type === "jc") {
      setHoverId(null);
      closeTooltip();
      return;
    }
    if (hoverId === box.id) return;

    closeTooltip();
    setHoverId(box.id);
    hoverTimerRef.current = setTimeout(() => openTooltip(box, row, clientX, clientY), 150);
  }, [findBoxAt, hoverId, closeTooltip, openTooltip]);

  const handleWheelZoom = useCallback((delta) => {
    setWidthZoom(prev => {
      const factor = delta < 0 ? WIDTH_ZOOM_STEP : 1 / WIDTH_ZOOM_STEP;
      return Math.min(WIDTH_ZOOM_MAX, Math.max(WIDTH_ZOOM_MIN, prev * factor));
    });
  }, []);

  const applyActivitySearch = useCallback((committed) => {
    setSearch({ order: committed?.order ?? "", item: committed?.item ?? "" });
    setMatchIndex(0);
  }, []);

  const stepMatch = useCallback((delta) => {
    if (matches.length === 0) return;

    const nextIndex = (matchIndex + delta + matches.length) % matches.length;
    setMatchIndex(nextIndex);

    const { box, row } = matches[nextIndex];
    selectBox(box, row);
    const left = scale ? Math.max(0, scale.x(box.s) - 240) : 0;
    frameApiRef.current?.scrollTo({ left, top: Math.max(0, row.top - row.height) });
  }, [matches, matchIndex, scale, selectBox]);

  // ── 하단 분할 (자원 간트 §7-14와 동일) ──────────────────────────────────────
  const toggleDetail = useCallback(() => {
    setDetailHeight(prev => (prev >= DETAIL_COLLAPSE_THRESHOLD ? DETAIL_TAB_BAR_HEIGHT : DETAIL_OPEN_HEIGHT));
  }, []);

  const handleDetailDragStart = useCallback((event) => {
    event.preventDefault();
    detailDragRef.current = { y: event.clientY, height: detailHeight };

    const onMove = (moveEvent) => {
      const drag = detailDragRef.current;
      if (!drag) return;
      setDetailHeight(Math.max(
        DETAIL_TAB_BAR_HEIGHT,
        Math.min(DETAIL_MAX_OPEN, drag.height - (moveEvent.clientY - drag.y))
      ));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      detailDragRef.current = null;
      setDetailHeight(prev => (prev < DETAIL_COLLAPSE_THRESHOLD ? DETAIL_TAB_BAR_HEIGHT : prev));
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [detailHeight]);

  // ── 조회조건 연동 (자원 간트와 동일 규칙) ────────────────────────────────────
  useEffect(() => {
    setResourceDraft(null);
    if (!versionState.planScope) {
      setResourceState({ selected: [], options: [] });
      return undefined;
    }

    let cancelled = false;
    const reqId = ++latestResourceReqRef.current;

    fpCommonService.getResources({ viewId: viewName, subId: "", planScope: versionState.planScope, resourceCdNm: "" })
      .then(response => {
        if (cancelled || reqId !== latestResourceReqRef.current) return;
        const options = (response.data ?? []).map(r => ({ value: r.resourceCd, label: r.resourceNm ?? r.resourceCd }));
        setResourceState({ selected: options.map(o => o.value), options });
      })
      .catch(e => debug(e));

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.planScope]);

  const selectedResourceOptions = useMemo(() => {
    if (resourceDraft) return resourceDraft;
    const picked = new Set(resourceState.selected);
    return resourceState.options.filter(option => picked.has(option.value));
  }, [resourceDraft, resourceState.options, resourceState.selected]);

  const allResourcesSelected = resourceState.options.length > 0
    && selectedResourceOptions.length === resourceState.options.length;

  const commitResourceSelection = useCallback(() => {
    setResourceDraft(null);
    if (!resourceDraft) return;

    const picked = new Set(resourceDraft.map(option => option.value));
    setResourceState(prev => {
      const next = prev.options.filter(option => picked.has(option.value)).map(option => option.value);
      const same = next.length === prev.selected.length && next.every((cd, i) => cd === prev.selected[i]);
      return same ? prev : { ...prev, selected: next };
    });
  }, [resourceDraft]);

  const renderResourcePaper = useCallback(({ children, ...paperProps }) => (
    <Paper {...paperProps}>
      <Box
        sx={{ display: "flex", gap: 0.5, px: 1, py: 0.5, borderBottom: 1, borderColor: "divider" }}
        onMouseDown={(event) => event.preventDefault()}
      >
        <Button size="small" onClick={() => setResourceDraft(resourceState.options)}>{t("selectAll")}</Button>
        <Button size="small" color="inherit" onClick={() => setResourceDraft([])}>{t("clearAll")}</Button>
      </Box>
      {children}
    </Paper>
  ), [resourceState.options, t]);

  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    setPeriodState(prev => ({
      ...prev, startDate, endDate: new Date(startDate.getTime() + SEARCH_ZONE_DAYS * 24 * 60 * 60 * 1000)
    }));
    requestRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();
    setRawRows([]);
    setBound(null);
    setFrozenZone(null);
    setLoading(false);
    // 옛 허용 자원이 남으면 다음 조회 전까지 드롭 판정이 틀린 값을 쓴다(effect를 걷어내면서
    // 초기화 자리가 사라졌으므로 여기서 챙긴다).
    altResourcesRef.current = {};
    jcIndexRef.current = null;
    closeTooltip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    requestRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodState.startDate, periodState.endDate, periodState.isEntirePlanPeriod, versionState.plantCd, resourceState.selected]);

  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  // 되돌리기 — **버튼은 두지 않는다**(2026-08-28 사용자 지시로 툴바에서 제거). 저장 경로가 없는
  // 화면이라 실수를 되돌릴 수단이 하나는 있어야 해서 표준 단축키로 남긴다.
  // 입력 컨트롤 안에서는 브라우저의 텍스트 되돌리기가 우선이므로 가로채지 않는다.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || String(event.key).toLowerCase() !== "z") return;

      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;

      event.preventDefault();
      undo();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [undo]);

  const editDisabled = !selected.box;

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        {/* `hasIcons={false}` — 우측에 밀어 붙일 액션이 없다. 안 주면 FilterContainer가 **마지막 자식**을
            `ml:auto`로 우측 정렬해서 자원 멀티셀렉트가 혼자 오른쪽 끝으로 날아간다(표시 배율 묶음을
            툴바 줄로 내리면서 자원이 마지막 자식이 됐다). */}
        <FilterContainer hasIcons={false}>
          <VersionSearchGroup
            value={versionState}
            onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
          />
          <PeriodSearchGroup
            value={periodState}
            onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
            useBucket={false}
          />
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            disableClearable
            options={resourceState.options}
            getOptionLabel={(o) => o?.label ?? ""}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            value={selectedResourceOptions}
            onChange={(_, options) => setResourceDraft(options)}
            onClose={commitResourceSelection}
            PaperComponent={renderResourcePaper}
            renderTags={(value) => value.length === 0 ? null : (
              <Box component="span" sx={{ pl: 0.75, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {allResourcesSelected
                  ? t("ALL")
                  : value.length === 1 ? value[0].label : `${value[0].label} +${value.length - 1}`}
              </Box>
            )}
            renderOption={(props, option, { selected: isSelected }) => (
              <Box component="li" {...props} key={option.value} sx={{ gap: 0.5 }}>
                <Checkbox size="small" checked={isSelected} sx={{ p: 0.25 }} />
                {option.label}
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label={t("FP_RESOURCE")} />}
            sx={{ ml: 1.5, minWidth: 250 }}
          />
        </FilterContainer>

        {/* ── 툴바 한 줄: 좌 = 보정 조작 묶음 / 우 = 표시 배율 묶음 ────────────────────
            old 구성 그대로다(2026-08-28 사용자 지시, 캡처 대조) — old도 `ButtonArea` 한 줄의
            `LeftButtonArea`/`RightButtonArea`로 좌우를 갈랐다. 이전에는 표시 배율 묶음이 **조회조건
            줄**에 얹혀 있었는데, 조회조건(무엇을 가져올까)과 표시 배율(어떻게 볼까)은 성격이 다르고
            줄이 갈리면 눈이 두 번 움직인다. 두 묶음 모두 같은 pill(`CTRL_GROUP_SX`)을 써서
            "이건 조작 묶음"이라는 계열을 공유한다.
            조회 버튼은 두지 않는다 — 조회조건이 바뀌면 자동 조회가 나가고(자원 간트와 같은 규칙),
            보정분이 있으면 그 경로에서 확인창이 뜬다(`requestRetrieve`). */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>

          {/* 보정 조작 묶음 — old `LeftButtonArea`(붙이기/이동/전체 밀기 + 되돌리기/저장).
              선택이 없으면 이동 계열이 전부 비활성(old `GanttButton`의 `disabled` 규칙). */}
          <Box sx={{ ...CTRL_GROUP_SX, gap: 0.25 }}>
            <Tooltip title={t("FP_ATTACH_LEFT")}>
              <span><IconButton color="info" size="small" disabled={editDisabled} onClick={() => runButtonMove("attach", "left")}><FirstPageIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title={t("FP_MOVE_2X_LEFT")}>
              <span><IconButton color="info" size="small" disabled={editDisabled} onClick={() => runButtonMove("move", "left", true)}><KeyboardDoubleArrowLeftIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title={t("FP_MOVE_LEFT")}>
              <span><IconButton color="info" size="small" disabled={editDisabled} onClick={() => runButtonMove("move", "left")}><KeyboardArrowLeftIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Chip size="small" label={t("FP_ACTIVITY")} />
            <Tooltip title={t("FP_MOVE_RIGHT")}>
              <span><IconButton color="info" size="small" disabled={editDisabled} onClick={() => runButtonMove("move", "right")}><KeyboardArrowRightIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title={t("FP_MOVE_2X_RIGHT")}>
              <span><IconButton color="info" size="small" disabled={editDisabled} onClick={() => runButtonMove("move", "right", true)}><KeyboardDoubleArrowRightIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title={t("FP_ATTACH_RIGHT")}>
              <span><IconButton color="info" size="small" disabled={editDisabled} onClick={() => runButtonMove("attach", "right")}><LastPageIcon fontSize="small" /></IconButton></span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.75 }} />

            {/* 전체 당기기 2종(§7-57) — **선택 작업이 있는 행 하나**가 범위다.
                `direction` 은 **어느 쪽을 당기는가**라 버튼 이름과 그대로 맞는다
                (old 는 `shiftType` 에 이동 **방향**을 넣어 좌우가 뒤집혀 있었다).

                ⚠️ **아이콘은 `direction` 의 반대쪽을 가리킨다**(2026-09-17 정정). `VerticalAlignTop`
                은 "막대 + 그쪽으로 향하는 화살표" = **이 끝에 맞춘다**는 뜻인데, 당겨지는 작업이
                움직이는 방향은 **기준 작업 쪽**이기 때문이다 — 왼쪽을 당기면 작업들이 오른쪽으로
                온다. 이름(`LEFT`)과 아이콘(→)이 반대로 보이지만 그게 맞다. */}
            <Tooltip title={t("FP_PULL_LEFT_FULL")}>
              <span><IconButton color="info" size="small" disabled={editDisabled} onClick={() => commitPull("left", selectedRowKey, selectedId)}><VerticalAlignTopIcon fontSize="small" sx={{ transform: "rotate(90deg)" }} /></IconButton></span>
            </Tooltip>
            <Tooltip title={t("FP_PULL_RIGHT_FULL")}>
              <span><IconButton color="info" size="small" disabled={editDisabled} onClick={() => commitPull("right", selectedRowKey, selectedId)}><VerticalAlignTopIcon fontSize="small" sx={{ transform: "rotate(-90deg)" }} /></IconButton></span>
            </Tooltip>
          </Box>

          {/* 변경 건수는 **묶음 밖**에 둔다 — 조작이 아니라 상태다. 면 안에 넣으면 버튼처럼 읽힌다.
              **0 일 때도 자리를 지킨다**(2026-09-16 사용자 요청) — 나타났다 사라지면 옆 컨트롤이
              밀리고, "변경이 없다"와 "이 화면엔 그런 표시가 없다"가 구분되지 않는다.
              대신 0 이면 색을 죽여 **강조는 변경이 있을 때만** 하도록 남긴다. */}
          <Chip
            size="small"
            color={changedCount > 0 ? "primary" : "default"}
            variant="outlined"
            label={`${t("FP_CHANGED")} ${changedCount}`}
          />

          {/* 표시 배율 묶음 — old `RightButtonArea`. 작업 검색도 여기 소속이다(§7-8). */}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
            <GanttViewControls
              t={t}
              startSlot={(
                <GanttActivitySearch
                  t={t}
                  value={search}
                  fields={SEARCH_FIELDS}
                  onCommit={applyActivitySearch}
                  matchCount={matches.length}
                  matchIndex={matchIndex}
                  onStep={stepMatch}
                />
              )}
              headerUnit={headerUnit}
              onHeaderUnitChange={setHeaderUnit}
              widthZoom={widthZoom}
              onWidthZoomChange={setWidthZoom}
              rowHeight={rowHeight}
              onRowHeightChange={setRowHeight}
              /* 이 줄엔 조회조건 접기 버튼이 없어 기본 우측 여백이 끝에 뜨는 틈이 된다. */
              sx={{ mr: 0 }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", flex: "1 1 0", minHeight: 0, mt: 1 }}>
          <Box sx={{ position: "relative", flex: "1 1 0", minHeight: 0, border: "1px solid", borderColor: "divider" }}>
            <GanttFrame
              rows={layout.rows}
              totalHeight={layout.totalHeight}
              columns={columns}
              scale={scale}
              ticks={ticks}
              selectedRowKey={selectedRowKey}
              loading={loading}
              loadingMessage={t("MSG_Loading")}
              drawBackground={handleDrawBackground}
              drawRow={handleDrawRow}
              drawOverlay={handleDrawOverlay}
              onRowActivate={handleRowActivate}
              onHoverPoint={handleHover}
              onWheelZoom={handleWheelZoom}
              onColumnFilterClick={(column, anchorEl) => setFilterAnchor(anchorEl)}
              filterTitle={t("filter")}
              onBoxDragStart={handleDragStart}
              onBoxDragMove={handleDragMove}
              onBoxDragEnd={handleDragEnd}
              onBoxDragCancel={handleDragCancel}
              /* 우클릭이 Slide 제스처이므로 브라우저 기본 메뉴를 막는다.
                 old도 이 화면엔 컨텍스트 메뉴가 없었다(`GanttMenu: ''`). */
              suppressContextMenu
              resolveCursor={resolveCursor}
              apiRef={frameApiRef}
            />
          </Box>
        </Box>

        <Box
          onMouseDown={handleDetailDragStart}
          sx={{
            flex: `0 0 ${DETAIL_GUTTER}px`, my: `${DETAIL_GUTTER_MARGIN}px`,
            backgroundColor: "#F5F5F5", cursor: "row-resize",
            display: "flex", alignItems: "center", justifyContent: "center",
            "&::after": {
              content: '""', width: 24, height: 2, borderTop: "1px dotted", borderBottom: "1px dotted",
              borderColor: "grey.500"
            }
          }}
        />

        <Box sx={{ display: "flex", flexDirection: "column", flex: `0 0 ${detailHeight}px`, minHeight: 0, overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", flex: `0 0 ${DETAIL_TAB_BAR_HEIGHT}px` }}>
            <Tooltip title={detailCollapsed ? t("FP_EXPAND") : t("FP_COLLAPSE")}>
              <IconButton size="small" onClick={toggleDetail} sx={{ mx: 0.5 }}>
                {detailCollapsed ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tabs
              value={detailTab}
              onChange={(_, value) => setDetailTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: DETAIL_TAB_BAR_HEIGHT }}
            >
              {DETAIL_TABS.map(tab => (
                <Tab key={tab.key} value={tab.key} label={t(tab.labelKey)} sx={{ minHeight: DETAIL_TAB_BAR_HEIGHT, py: 0.75, fontSize: "0.875rem" }} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ flex: "1 1 0", minHeight: 0, overflow: "hidden", pt: detailCollapsed ? 0 : `${DETAIL_CONTENT_GAP}px` }}>
            {!detailCollapsed ? (
              <>
                {detailTab === "adjust" && (
                  <AdjustDetailTab
                    t={t}
                    box={selected.box}
                    row={selected.row}
                    altResources={selectedAltResources}
                    onMove={handleDetailMove}
                  />
                )}
                {detailTab === "plan" && (
                  <ResourcePlanDetailTab rows={resourcePlanRows} viewName={`${viewName}_PLAN_DETAIL`} t={t} />
                )}
                {detailTab === "psi" && (
                  <PsiTab
                    active
                    activity={selected.box?.activity ?? null}
                    versionState={versionState}
                    viewName={`${viewName}_PSI`}
                    t={t}
                  />
                )}
              </>
            ) : null}
          </Box>
        </Box>
      </Box>

      <GanttColumnFilter
        t={t}
        anchorEl={filterAnchor}
        label={t("FP_RESOURCE")}
        options={resourceFilterOptions}
        value={resourceFilter}
        onCommit={setResourceFilter}
        onClose={() => setFilterAnchor(null)}
      />

      <GanttTooltip tooltip={tooltip} t={t} />

      {/* 조회 시 보정분 소실 확인 — `title`이 "confirm"이면 공용 Dialog가 표준 확인 문구를 쓴다. */}
      <ConfirmDialog
        open={confirmOpen}
        title="confirm"
        content={t("FP_MSG_DISCARD_ADJUSTMENT")}
        onClose={() => setConfirmOpen(false)}
        onHandler={(response) => { if (response) debouncedRetrieveRef.current(); }}
      />

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(AdjustmentGantt);
